"use client"

import { useState, useContext, useEffect, useMemo, useRef, useCallback } from "react"
import BuscadorItemsUbicaciones from "./BuscadorItemsUbicaciones.js"
import { ErpContext } from "../../context/ErpContext.js"
import { formatInputWithSeparators, parseLocalizedNumber, getLocaleConfig } from "../../utils/numeros.js"
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Chip,
  Alert,
  Fade,
  alpha,
  useTheme,
  Tooltip,
  Avatar,
  InputAdornment,
  Grid,
  Paper,
  Stack,
  Autocomplete,
} from "@mui/material"

// Icons
import DeleteIcon from "@mui/icons-material/Delete"
import SaveIcon from "@mui/icons-material/Save"
import UpdateIcon from "@mui/icons-material/Update"
import InventoryIcon from "@mui/icons-material/Inventory"
import ScaleIcon from "@mui/icons-material/Scale"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import InfoIcon from "@mui/icons-material/Info"
import GroupsIcon from "@mui/icons-material/Groups"
import BallotIcon from "@mui/icons-material/Ballot"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import PercentIcon from "@mui/icons-material/Percent"
import CalculateIcon from "@mui/icons-material/Calculate"
import CircularProgress from "@mui/material/CircularProgress"
import SpinnerLoading from "../misc/SpinnerComponent.js"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs from "dayjs"
import ShowChartIcon from "@mui/icons-material/ShowChart"
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn"
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"
import AssessmentIcon from "@mui/icons-material/Assessment"
import TimelineIcon from "@mui/icons-material/Timeline"
import PieChartIcon from "@mui/icons-material/PieChart"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff"
import BusinessIcon from "@mui/icons-material/Business"

import {
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
} from "recharts"

// ✅ FUNCIÓN HELPER: Detectar formato de fecha según la configuración regional del navegador
const getDateFormatByLocale = () => {
  // Detectar la locale del navegador
  const locale = navigator.language || navigator.languages?.[0] || 'en-US';
  
  // 🔍 DEBUG: Logging para diagnóstico
  //console.log('🌍 Locale detectado del navegador:', locale);
  
  // 🌎 PRIORIDAD 1: Detectar por zona horaria (para Argentina forzar DD/MM/YYYY)
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    //console.log('🕐 Zona horaria detectada:', timeZone);
    
    // Si la zona horaria es de Argentina, forzar formato DD/MM/YYYY
    if (timeZone && timeZone.includes('Argentina')) {
      //console.log('✅ Formato aplicado: DD/MM/YYYY (por zona horaria Argentina)');
      return 'DD/MM/YYYY';
    }
    
    // Si la zona horaria es de México, forzar formato DD/MM/YYYY
    if (timeZone && timeZone.includes('Mexico')) {
      //console.log('✅ Formato aplicado: DD/MM/YYYY (por zona horaria México)');
      return 'DD/MM/YYYY';
    }
    
    // Si la zona horaria es de España, forzar formato DD/MM/YYYY
    if (timeZone && timeZone.includes('Madrid')) {
      //console.log('✅ Formato aplicado: DD/MM/YYYY (por zona horaria España)');
      return 'DD/MM/YYYY';
    }
  } catch (error) {
    console.warn('⚠️ Error detectando zona horaria:', error);
  }
  
  // 🗺️ PRIORIDAD 2: Mapeo de locales a formatos de fecha para dayjs
  const localeFormatMap = {
    // Español (Argentina, España, etc.) -> DD/MM/YYYY
    'es-AR': 'DD/MM/YYYY',
    'es-ES': 'DD/MM/YYYY',
    'es-MX': 'DD/MM/YYYY',
    'es-CL': 'DD/MM/YYYY',
    'es-CO': 'DD/MM/YYYY',
    'es-PE': 'DD/MM/YYYY',
    'es-VE': 'DD/MM/YYYY',
    'es': 'DD/MM/YYYY',
    
    // Inglés USA -> MM/DD/YYYY
    'en-US': 'MM/DD/YYYY',
    
    // Inglés Reino Unido -> DD/MM/YYYY
    'en-GB': 'DD/MM/YYYY',
    'en-AU': 'DD/MM/YYYY',
    'en-CA': 'DD/MM/YYYY',
    
    // Alemán -> DD.MM.YYYY
    'de-DE': 'DD.MM.YYYY',
    'de': 'DD.MM.YYYY',
    
    // Francés -> DD/MM/YYYY
    'fr-FR': 'DD/MM/YYYY',
    'fr': 'DD/MM/YYYY',
    
    // Italiano -> DD/MM/YYYY
    'it-IT': 'DD/MM/YYYY',
    'it': 'DD/MM/YYYY',
    
    // Portugués -> DD/MM/YYYY
    'pt-BR': 'DD/MM/YYYY',
    'pt-PT': 'DD/MM/YYYY',
    'pt': 'DD/MM/YYYY',
  };
  
  // Buscar formato exacto
  if (localeFormatMap[locale]) {
    //console.log(`✅ Formato aplicado: ${localeFormatMap[locale]} (por locale exacto: ${locale})`);
    return localeFormatMap[locale];
  }
  
  // Buscar por código de idioma (sin región)
  const languageCode = locale.split('-')[0];
  if (localeFormatMap[languageCode]) {
    //console.log(`✅ Formato aplicado: ${localeFormatMap[languageCode]} (por código de idioma: ${languageCode})`);
    return localeFormatMap[languageCode];
  }
  
  // 🔍 PRIORIDAD 3: Detectar formato usando Intl.DateTimeFormat
  try {
    const formatter = new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    
    // Crear una fecha de prueba para ver el formato
    const testDate = new Date(2025, 0, 15); // 15 de enero de 2025
    const formatted = formatter.format(testDate);
    //console.log('📅 Fecha de prueba formateada:', formatted);
    
    // Detectar el formato basado en la posición del día y mes
    if (formatted.startsWith('15')) {
      // El día viene primero -> DD/MM/YYYY o DD.MM.YYYY
      const detectedFormat = formatted.includes('.') ? 'DD.MM.YYYY' : 'DD/MM/YYYY';
      //console.log(`✅ Formato aplicado: ${detectedFormat} (detectado automáticamente)`);
      return detectedFormat;
    } else if (formatted.startsWith('01')) {
      // El mes viene primero -> MM/DD/YYYY
      //console.log('✅ Formato aplicado: MM/DD/YYYY (detectado automáticamente)');
      return 'MM/DD/YYYY';
    } else if (formatted.startsWith('2025')) {
      // El año viene primero -> YYYY-MM-DD
      //console.log('✅ Formato aplicado: YYYY-MM-DD (detectado automáticamente)');
      return 'YYYY-MM-DD';
    }
  } catch (error) {
    console.warn('⚠️ Error detectando formato de fecha, usando DD/MM/YYYY por defecto:', error);
  }
  
  // 📋 Formato por defecto: DD/MM/YYYY (más común en países de habla hispana)
  //console.log('✅ Formato aplicado: DD/MM/YYYY (formato por defecto)');
  return 'DD/MM/YYYY';
}

const RecetaComponent = ({ 
  modoEdicion = false,           // false = crear, true = editar
  paqueteInicial = null,         // datos del paquete a editar
  mostrarAnaliticos = true,      // mostrar/ocultar sección de análisis
  enModal = false,               // si está dentro de un modal
  onGuardadoExitoso = null,      // callback después de guardar
  onCancelar = null,             // callback para cancelar
  ocultarCantidadYCostoXCantidadInicial = null, // configuración inicial opcional
  onBaseItemChange = null,       // ✅ callback cuando cambia el grupo base seleccionado
}) => {
  const theme = useTheme()
  const { 
    getItemAndItemUbicacion, // Para buscar GRUPOS (esServicio: false)
    getItemAndItemUbicacionServicios, // Para buscar SERVICIOS (esServicio: true)
    getItemAtributoNoEliminados, 
    itemAtributoNoEliminados, // Para mostrar atributos
    crearRecetaApi, 
    setCerradoPaqueteApi,
    getBomByIdItem,
    getListaMontosByIdItemIdEntidad, // Para obtener precios/costos
    updateListaDeMonto, // Para actualizar precios/costos
    getEntidadFilterTipoEntidad, // Para obtener clientes (precios finales)
    getEntidadByID, // Para cargar proveedor por ID
    getEntidadesClientesTresLetras, // Para buscar proveedores por texto
    entidadesClientesTresLetras, // Array de entidades encontradas por búsqueda
    postItemUbicacion, // Para asociar item con ubicación
    updateItemUbicaciones, // Para actualizar stock de itemUbicacion (grupos)
    postListaMonto, // Para crear precio/costo inicial
    getItemUbicacionFilterByUbicacion, // Para verificar si item está asociado a ubicación
    ubicaciones, // Para obtener la ubicación por ID
    formatoNumericoGlobal, // Para formateo numérico regional
    getParametrosGlobales,
  } = useContext(ErpContext)

  const [ocultarCantidadYCostoXCantidad, setOcultarCantidadYCostoXCantidad] = useState(
    typeof ocultarCantidadYCostoXCantidadInicial === "boolean"
      ? ocultarCantidadYCostoXCantidadInicial
      : false
  )

  useEffect(() => {
    let cancelado = false

    if (typeof ocultarCantidadYCostoXCantidadInicial === "boolean") {
      setOcultarCantidadYCostoXCantidad(ocultarCantidadYCostoXCantidadInicial)
      return () => {
        cancelado = true
      }
    }

    const cargarParametro = async () => {
      try {
        const res = await getParametrosGlobales?.(["paquetesOcultarCantidadYCostoXCantidad"])
        const raw = res?.paquetesOcultarCantidadYCostoXCantidad
        const flag = raw === true || raw === 1 || raw === "1" || raw === "true" || raw === "TRUE"
        if (!cancelado) setOcultarCantidadYCostoXCantidad(Boolean(flag))
      } catch (e) {
        // Si falla, dejamos el default false.
      }
    }

    cargarParametro()
    return () => {
      cancelado = true
    }
  }, [getParametrosGlobales, ocultarCantidadYCostoXCantidadInicial])

  const [baseItem, setBaseItem] = useState(null)
  const [ingredientes, setIngredientes] = useState([]) // Cada ingrediente incluirá { ...item, idEntidad }
  const [cantidades, setCantidades] = useState({})
  const [searchFilters, setSearchFilters] = useState(null)
  const [recetaExistente, setRecetaExistente] = useState(false)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  
  // Estados para gestión de proveedores
  const [proveedoresDisponibles, setProveedoresDisponibles] = useState([])
  const [proveedorGenerico, setProveedorGenerico] = useState(null)
  const [entidadesServicios, setEntidadesServicios] = useState({}) // { [idItem]: idEntidad }
  const [loadingProveedores, setLoadingProveedores] = useState(false)

  // 🔒 Estado de bloqueo por paquete cerrado (manual)
  const [paqueteCerrado, setPaqueteCerrado] = useState(Boolean(paqueteInicial?.cerrado))
  const [loadingCerrado, setLoadingCerrado] = useState(false)
  const bloqueado = modoEdicion && paqueteCerrado
  const ocultarDestino = Boolean(modoEdicion || recetaExistente)
  const mostrarBarraUnificada = true
  const [resetFiltrosKey, setResetFiltrosKey] = useState(0)

  const handleResetFiltrosBarra = () => {
    // Resetea filtros del buscador (internos) + resetea los campos del paquete en la barra
    setResetFiltrosKey((k) => k + 1)
    setFechaInicio(new Date())
    setFechaFin(null)
    setDiasDuracion("")
    setNoches("")
    setPrecioFinalPaqueteDisplay("")
    setPrecioFinalPaquete("")
    setPrecioFinalEditado(false)
    setStockEditadoDisplay("")
    setStockEditado("")
    setStockEditadoFlag(false)
  }
  
  // Nuevos estados para ubicación y costos
  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const [preciosCostos, setPreciosCostos] = useState({}) // { [key]: { costo: Y, idEntidadCosto: W } }
  const [costosEditados, setCostosEditados] = useState({}) // Para tracking de cambios en costos de servicios
  const [precioFinalPaquete, setPrecioFinalPaquete] = useState(0) // Precio del paquete completo (del grupo) - valor numérico
  const [precioFinalPaqueteDisplay, setPrecioFinalPaqueteDisplay] = useState("") // Precio formateado para mostrar
  const [savingPrices, setSavingPrices] = useState(false)
  const [clientesEntidades, setClientesEntidades] = useState([])
  const [precioFinalEditado, setPrecioFinalEditado] = useState(false) // Track si el precio del paquete fue editado
  const [stockEditado, setStockEditado] = useState(0) // Stock editado del grupo - valor numérico
  const [stockEditadoDisplay, setStockEditadoDisplay] = useState("") // Stock formateado para mostrar
  const [stockEditadoFlag, setStockEditadoFlag] = useState(false) // Track si el stock fue editado
  const [fechaInicio, setFechaInicio] = useState(new Date()) // Fecha Inicio del paquete (obligatoria)
  const [fechaFin, setFechaFin] = useState(null) // Fecha de fin del paquete (opcional)
  const [diasDuracion, setDiasDuracion] = useState("") // Días de duración del paquete
  const [noches, setNoches] = useState("") // Número de noches del viaje
  
  // ✅ Detectar formato de fecha según la configuración regional del navegador
  const dateFormat = getDateFormatByLocale()
  
  // Configuración de locale para formateo numérico
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  )
  
  // Efecto para sincronizar precioFinalPaqueteDisplay cuando cambia precioFinalPaquete
  useEffect(() => {
    if (precioFinalPaquete && precioFinalPaquete !== 0 && precioFinalPaquete !== "") {
      setPrecioFinalPaqueteDisplay(formatInputWithSeparators(precioFinalPaquete, localeConfig.locale))
    } else if (precioFinalPaquete === 0 || precioFinalPaquete === "") {
      setPrecioFinalPaqueteDisplay("")
    }
  }, [precioFinalPaquete, localeConfig.locale])
  
  // Efecto para sincronizar stockEditadoDisplay cuando cambia stockEditado
  useEffect(() => {
    if (stockEditado && stockEditado !== 0 && stockEditado !== "") {
      setStockEditadoDisplay(formatInputWithSeparators(stockEditado, localeConfig.locale))
    } else if (stockEditado === 0 || stockEditado === "") {
      setStockEditadoDisplay("")
    }
  }, [stockEditado, localeConfig.locale])

  const page = 1

  // 🔧 REF: Para controlar carga única del proveedor genérico
  const proveedorGenericoCargado = useRef(false)

  // 🔧 USEEFFECT: Cargar proveedor genérico SOLO UNA VEZ
  useEffect(() => {
    if (proveedorGenericoCargado.current) return
    
    proveedorGenericoCargado.current = true
    
    const cargarProveedorGenerico = async () => {
      try {
        const provGenerico = await getEntidadByID(2)
        if (provGenerico) {
          setProveedorGenerico(provGenerico)
          setProveedoresDisponibles([provGenerico])
        }
      } catch (error) {
        console.error("Error cargando proveedor genérico:", error)
        proveedorGenericoCargado.current = false // Permitir reintento si falla
      }
    }
    cargarProveedorGenerico()
  }, []) // ✅ Sin dependencias - solo se ejecuta al montar

  // 🔧 USEEFFECT: Sincronizar proveedores encontrados con búsqueda dinámica
  useEffect(() => {
    // When new providers are fetched via getEntidadesClientesTresLetras, replace old search results
    if (entidadesClientesTresLetras && entidadesClientesTresLetras.length > 0) {
      console.log('📋 Proveedores encontrados en búsqueda:', entidadesClientesTresLetras)
      setProveedoresDisponibles((prev) => {
        // Obtener IDs de proveedores actualmente en uso (seleccionados en la tabla)
        const idsEnUso = new Set(
          ingredientes
            .map(ing => ing.idEntidad || entidadesServicios[ing.idItem])
            .filter(Boolean)
        )
        
        // Mantener proveedores que están en uso
        const proveedoresEnUso = prev.filter(p => idsEnUso.has(p.id))
        
        // Crear un mapa de IDs en uso para evitar duplicados
        const idsExistentes = new Set(proveedoresEnUso.map(p => p.id))
        
        // Agregar nuevos resultados de búsqueda (sin duplicar)
        const nuevosResultados = entidadesClientesTresLetras.filter(
          (newProv) => !idsExistentes.has(newProv.id)
        )
        
        console.log('✅ Reemplazando resultados de búsqueda:', {
          enUso: proveedoresEnUso.length,
          nuevos: nuevosResultados.length,
          total: proveedoresEnUso.length + nuevosResultados.length
        })
        
        // Retornar: proveedores en uso + nuevos resultados de búsqueda
        return [...proveedoresEnUso, ...nuevosResultados]
      })
      setLoadingProveedores(false)
    }
  }, [entidadesClientesTresLetras, ingredientes, entidadesServicios])

  // ✅ OPTIMIZADO: Función para cargar precio del paquete desde ListaDeMontos (MOVIDA AQUÍ ANTES DE precargarDatosPaquete)
  const cargarPrecioPaquete = useCallback(async (idItem, idUbicacion) => {
    try {
      // Usar idEntidad hardcodeado = 1 para clientes
      const precioData = await getListaMontosByIdItemIdEntidad(1, idItem, idUbicacion)
      console.log(`📋 Precio cargado para ${idItem} en ${idUbicacion}:`, precioData)
      if (precioData && precioData.monto) {
        const precioNum = Number.parseFloat(precioData.monto)
        setPrecioFinalPaquete(precioNum)
        setPrecioFinalPaqueteDisplay(formatInputWithSeparators(precioNum, localeConfig.locale))
        setPrecioFinalEditado(false) // Reset del flag de editado
      }
    } catch (error) {
      //console.log("ℹ️ No se encontró precio para el paquete (normal para paquetes nuevos)")
      setPrecioFinalPaquete(0)
      setPrecioFinalPaqueteDisplay("")
    }
  }, [getListaMontosByIdItemIdEntidad, localeConfig.locale])
  
  // ✅ OPTIMIZADO: Función para cargar stock del grupo desde ItemUbicacion (MOVIDA AQUÍ ANTES DE precargarDatosPaquete)
  const cargarStockGrupo = useCallback(async (idItem, idUbicacion) => {
    try {
      const itemUbicaciones = await getItemUbicacionFilterByUbicacion(idItem, idUbicacion)
      if (itemUbicaciones && itemUbicaciones.length > 0) {
        const stock = itemUbicaciones[0].inventario || 0
        setStockEditado(stock)
        setStockEditadoDisplay(formatInputWithSeparators(stock, localeConfig.locale))
        setStockEditadoFlag(false) // Reset del flag de editado
        //console.log(`📦 Stock cargado para grupo ${idItem}: ${stock}`)
      }
    } catch (error) {
      //console.log("ℹ️ No se encontró stock para el grupo (normal para grupos nuevos)")
      setStockEditado(0)
      setStockEditadoDisplay("")
    }
  }, [getItemUbicacionFilterByUbicacion, localeConfig.locale])
  
  // Función para cargar costos de servicios
  const cargarPreciosCostosServicios = useCallback(async (servicios, idUbicacion) => {
    try {
      console.log(`🔍 Cargando costos para ${servicios.length} servicios`)
      
      for (const servicio of servicios) {
        const key = `${servicio.idItem}-${idUbicacion}`
        const descripcion = servicio.descripcion || servicio.codigo || `ID:${servicio.idItem}`
        
        // Si ya tiene costo cargado de BOM, no sobrescribir
        if (preciosCostos[key]?.fromBOM) {
          console.log(`  ⏭️ ${descripcion}: Ya tiene costo de BOM, NO recargar`)
          continue
        }
        
        // Buscar en ListaDeMontos
        try {
          const idEntidad = servicio.idEntidad || entidadesServicios[servicio.idItem] || 2
          console.log(`  📋 ${descripcion}: Buscando en ListaDeMontos con idEntidad=${idEntidad}`)
          
          const costoData = await getListaMontosByIdItemIdEntidad(idEntidad, servicio.idItem, idUbicacion)
          const costo = costoData?.monto ? Number.parseFloat(costoData.monto) : 0
          
          console.log(`  ✅ ${descripcion}: Costo encontrado = $${costo}`)
          
          setPreciosCostos(prev => ({
            ...prev,
            [key]: {
              costo,
              idEntidadCosto: idEntidad,
              fromBOM: false
            }
          }))
        } catch (error) {
          console.log(`  ⚠️ ${descripcion}: No se encontró en ListaDeMontos, usando $0`)
          setPreciosCostos(prev => ({
            ...prev,
            [key]: {
              costo: 0,
              idEntidadCosto: servicio.idEntidad || 2,
              fromBOM: false
            }
          }))
        }
      }
      
      console.log('✅ Costos cargados')
    } catch (error) {
      console.error("❌ Error cargando costos:", error)
    }
  }, [getListaMontosByIdItemIdEntidad, entidadesServicios, preciosCostos])

  // ✅ OPTIMIZADO: Pre-cargar datos del paquete en modo edición con useCallback (MOVIDO ANTES DEL useEffect)
  const precargarDatosPaquete = useCallback(async () => {
    try {
      setLoading(true)
      await getItemAtributoNoEliminados()
      
      //console.log("📦 Paquete inicial recibido:", paqueteInicial)
      
      // 1. Pre-cargar ubicación/destino
      let ubicacionPreCargada = null
      if (paqueteInicial.destino && ubicaciones) {
        ubicacionPreCargada = ubicaciones.find(u => u.descripcion === paqueteInicial.destino)
        if (ubicacionPreCargada) {
          setSelectedUbicacion(ubicacionPreCargada)
          //console.log("📍 Ubicación pre-cargada:", ubicacionPreCargada)
        }
      }

      // 2. Pre-cargar grupo (baseItem) - MEJORADO
      if (paqueteInicial.idProductoTerminado) {
        //console.log("🔍 Buscando grupo con ID:", paqueteInicial.idProductoTerminado)
        
        // Usar la función correctamente con los parámetros en orden correcto:
        // getItemAndItemUbicacion(page, limit, ubicacionId, itemId, precio, soloStockBajo, mostrarUltimosCreados, mostrarRecetas, proveedorId, searchText)
        const items = await getItemAndItemUbicacion(
          1, // page
          100, // limit
          null, // ubicacionId
          paqueteInicial.idProductoTerminado, // itemId
          null, // precio
          false, // soloStockBajo
          false, // mostrarUltimosCreados
          false, // mostrarRecetas
          null, // proveedorId
          null  // searchText
        )
        
        //console.log("📦 Items encontrados:", items)
        
        // La respuesta viene con formato de paginación: { data: [...], total, currentPage, totalPages }
        const itemsData = items?.data || items
        
        if (itemsData && itemsData.length > 0) {
          const grupoBase = itemsData[0]
          setBaseItem(grupoBase)
          if (onBaseItemChange) {
            onBaseItemChange(grupoBase) // ✅ Notificar al padre
          }
          //console.log("✅ Grupo pre-cargado:", grupoBase)
          
          // Cargar precio del paquete
          if (ubicacionPreCargada) {
            await cargarPrecioPaquete(grupoBase.idItem, ubicacionPreCargada.id)
            await cargarStockGrupo(grupoBase.idItem, ubicacionPreCargada.id)
          }
        } else {
          console.warn("⚠️ No se encontró el grupo con ID:", paqueteInicial.idProductoTerminado)
          //console.log("Datos recibidos:", itemsData)
        }
      }

      // 3. Pre-cargar fechas - MEJORADO (convertir de DD/MM/YYYY a Date)
      if (paqueteInicial.fechaInicio) {
        try {
          // Si viene como "DD/MM/YYYY", convertir a Date
          if (typeof paqueteInicial.fechaInicio === 'string' && paqueteInicial.fechaInicio.includes('/')) {
            const [day, month, year] = paqueteInicial.fechaInicio.split('/')
            setFechaInicio(new Date(year, month - 1, day))
            //console.log("📅 Fecha inicio pre-cargada (convertida):", `${year}-${month}-${day}`)
          } else {
            setFechaInicio(new Date(paqueteInicial.fechaInicio))
            //console.log("📅 Fecha inicio pre-cargada")
          }
        } catch (e) {
          console.error("Error parseando fecha inicio:", e)
        }
      }
      
      if (paqueteInicial.fechaFin && paqueteInicial.fechaFin !== 'N/A') {
        try {
          // Si viene como "DD/MM/YYYY", convertir a Date
          if (typeof paqueteInicial.fechaFin === 'string' && paqueteInicial.fechaFin.includes('/')) {
            const [day, month, year] = paqueteInicial.fechaFin.split('/')
            const fechaFinCargada = new Date(year, month - 1, day)
            setFechaFin(fechaFinCargada)
            // Calcular días si hay fecha inicio
            if (paqueteInicial.fechaInicio) {
              let fechaInicioCargada
              if (typeof paqueteInicial.fechaInicio === 'string' && paqueteInicial.fechaInicio.includes('/')) {
                const [dayIni, monthIni, yearIni] = paqueteInicial.fechaInicio.split('/')
                fechaInicioCargada = new Date(yearIni, monthIni - 1, dayIni)
              } else {
                fechaInicioCargada = new Date(paqueteInicial.fechaInicio)
              }
              const diferencia = Math.ceil((fechaFinCargada - fechaInicioCargada) / (1000 * 60 * 60 * 24))
              setDiasDuracion(diferencia > 0 ? diferencia.toString() : "")
            }
            //console.log("📅 Fecha fin pre-cargada (convertida):", `${year}-${month}-${day}`)
          } else {
            const fechaFinCargada = new Date(paqueteInicial.fechaFin)
            setFechaFin(fechaFinCargada)
            // Calcular días si hay fecha inicio
            if (paqueteInicial.fechaInicio) {
              const fechaInicioCargada = new Date(paqueteInicial.fechaInicio)
              const diferencia = Math.ceil((fechaFinCargada - fechaInicioCargada) / (1000 * 60 * 60 * 24))
              setDiasDuracion(diferencia > 0 ? diferencia.toString() : "")
            }
            //console.log("📅 Fecha fin pre-cargada")
          }
        } catch (e) {
          console.error("Error parseando fecha fin:", e)
        }
      }

      // 3.5. Pre-cargar noches
      if (paqueteInicial.noches !== undefined && paqueteInicial.noches !== null) {
        setNoches(paqueteInicial.noches.toString())
        //console.log("🌙 Noches pre-cargadas:", paqueteInicial.noches)
      }

      // 4. Pre-cargar servicios (ingredientes) con cantidades y proveedores
      if (paqueteInicial.ingredientes && paqueteInicial.ingredientes.length > 0) {
        const serviciosCompletos = paqueteInicial.ingredientes.map(ing => ({
          ...ing.materiaPrima,
          idItem: ing.idMateriaPrima,
          cantidad: ing.cantidad,
          idEntidad: ing.idEntidad || 2 // ✅ Usar idEntidad de la BOM, o genérico por defecto
        }))
        
        // ✅ Pre-cargar entidades de servicios
        const entidadesMap = {}
        const idsEntidadesUnicas = new Set()
        paqueteInicial.ingredientes.forEach(ing => {
          if (ing.idEntidad) {
            entidadesMap[ing.idMateriaPrima] = ing.idEntidad
            idsEntidadesUnicas.add(ing.idEntidad)
          }
        })
        setEntidadesServicios(entidadesMap)
        //console.log("🏢 Entidades (proveedores) pre-cargadas:", entidadesMap)
        
        // ✅ Cargar los proveedores únicos en proveedoresDisponibles
        const idsACargar = Array.from(idsEntidadesUnicas).filter(id => id !== 2) // Excluir genérico que ya está
        if (idsACargar.length > 0) {
          const proveedoresCargados = await Promise.all(
            idsACargar.map(id => getEntidadByID(id).catch(() => null))
          )
          const proveedoresValidos = proveedoresCargados.filter(p => p !== null)
          if (proveedoresValidos.length > 0) {
            setProveedoresDisponibles(prev => [...prev, ...proveedoresValidos])
          }
        }
        setIngredientes(serviciosCompletos)
        //console.log("🧩 Servicios pre-cargados:", serviciosCompletos)
        
        // Pre-cargar cantidades y costos (montos) desde la BOM
        if (ubicacionPreCargada) {
          const cantidadesMap = {}
          const costosMap = {}
          
          paqueteInicial.ingredientes.forEach(ing => {
            const key = `${ing.idMateriaPrima}-${ubicacionPreCargada.id}`
            cantidadesMap[key] = ing.cantidad.toString()
            
            // ✅ Pre-cargar monto guardado en BOM
            if (ing.monto !== undefined && ing.monto !== null) {
              const cantidad = parseFloat(ing.cantidad) || 1
              const costoUnitario = cantidad > 0 ? ing.monto / cantidad : 0
              costosMap[key] = {
                costo: costoUnitario,
                idEntidadCosto: ing.idEntidad || 2
              }
              // //console.log(`💰 Costo pre-cargado para ${ing.idMateriaPrima}: ${costoUnitario} (monto total: ${ing.monto})`)
            }
          })
          
          setCantidades(cantidadesMap)
          //console.log("📊 Cantidades pre-cargadas:", cantidadesMap)
          
          // ✅ Pre-cargar costos desde la BOM si existen
          if (Object.keys(costosMap).length > 0) {
            // ✅ Marcar costos de BOM con flag especial para NO sobrescribirlos
            const costosConFlag = {}
            Object.keys(costosMap).forEach(key => {
              costosConFlag[key] = { ...costosMap[key], fromBOM: true } // ✅ Marca de histórico
            })
            setPreciosCostos(costosConFlag)
            
            // 🔥 IMPORTANTE: Si algunos servicios NO tienen monto en BOM, cargarlos desde ListaDeMontos
            const serviciosSinMonto = serviciosCompletos.filter(servicio => {
              const key = `${servicio.idItem}-${ubicacionPreCargada.id}`
              return !costosConFlag[key] // Servicios que no tienen costo en BOM
            })
            
            if (serviciosSinMonto.length > 0) {
              console.log(`📋 Cargando costos de ListaDeMontos para ${serviciosSinMonto.length} servicios sin monto en BOM`)
              await cargarPreciosCostosServicios(serviciosSinMonto, ubicacionPreCargada.id, false) // NO forzar, solo llenar los faltantes
            }
          } else {
            // Si no hay montos guardados en BOM, cargar TODOS desde ListaDeMontos
            console.log('📋 No hay montos en BOM, cargando todos desde ListaDeMontos')
            await cargarPreciosCostosServicios(serviciosCompletos, ubicacionPreCargada.id, false) // NO forzar recarga
          }
        }
        
        setRecetaExistente(true)
      }

    } catch (error) {
      console.error("❌ Error pre-cargando datos del paquete:", error)
      setError("Error al cargar datos del paquete")
    } finally {
      setLoading(false)
    }
  }, [
    getItemAtributoNoEliminados, paqueteInicial, ubicaciones,
    getItemAndItemUbicacion, cargarPrecioPaquete, cargarStockGrupo,
    getBomByIdItem, cargarPreciosCostosServicios, getEntidadByID,
    onBaseItemChange
  ])

  // 🔥 OPTIMIZADO: useEffect para pre-cargar con useRef para evitar loops
  const precargarDatosCalled = useRef(false)
  
  useEffect(() => {
    if (!modoEdicion || !paqueteInicial) {
      precargarDatosCalled.current = false
      return
    }
    
    // Sincronizar estado cerrado
    setPaqueteCerrado(Boolean(paqueteInicial.cerrado))
    
    // Pre-cargar datos solo si hay ubicaciones Y no se ha llamado antes
    if (ubicaciones && ubicaciones.length > 0 && !precargarDatosCalled.current) {
      precargarDatosCalled.current = true
      //console.log("📝 Modo Edición: Pre-cargando datos del paquete", paqueteInicial)
      precargarDatosPaquete()
    }
  }, [modoEdicion, paqueteInicial?.id, ubicaciones?.length, precargarDatosPaquete])

  // 🔥 useEffect para cargar proveedores de todos los servicios automáticamente
  useEffect(() => {
    const cargarProveedoresFaltantes = async () => {
      const idsProveedores = new Set()
      
      // Recopilar todos los idEntidad únicos de los servicios
      ingredientes.forEach(servicio => {
        const idEntidad = servicio.idEntidad || entidadesServicios[servicio.idItem]
        if (idEntidad && idEntidad !== 2 && !proveedoresDisponibles.some(p => p.id === idEntidad)) {
          idsProveedores.add(idEntidad)
        }
      })
      
      // Cargar proveedores faltantes
      if (idsProveedores.size > 0) {
        console.log(`📋 Cargando ${idsProveedores.size} proveedores faltantes...`)
        const proveedoresCargados = await Promise.all(
          Array.from(idsProveedores).map(id => getEntidadByID(id).catch(() => null))
        )
        const proveedoresValidos = proveedoresCargados.filter(p => p !== null)
        if (proveedoresValidos.length > 0) {
          setProveedoresDisponibles(prev => {
            const nuevosProv = proveedoresValidos.filter(p => !prev.some(existing => existing.id === p.id))
            return nuevosProv.length > 0 ? [...prev, ...nuevosProv] : prev
          })
        }
      }
    }
    
    if (ingredientes.length > 0) {
      cargarProveedoresFaltantes()
    }
  }, [ingredientes, entidadesServicios, getEntidadByID])

  // ✅ OPTIMIZADO: Función para generar atributos como subtexto con useCallback
  const generarAtributosSubtexto = useCallback((item) => {
    if (!item || !itemAtributoNoEliminados) return ""
    
    const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)
    const attributeValues = activeAttributes
      .map((attr, index) => {
        const attributeValue = item[`itemDatoAtributo${index + 1}`]
        return attributeValue ? attributeValue : ""
      })
      .filter((value) => value !== "" && value !== "undefined")
      .join(" • ")
    
    return attributeValues
  }, [itemAtributoNoEliminados])

  // ✅ NUEVO: Construye la descripción completa del paquete para gastos automáticos
  // Formato: "{nombreGrupo} - {atributos} / {destino}"
  // Ejemplo: "Fatima 2025 - 5to Año / Bariloche"
  const construirDescripcionPaquete = useCallback(() => {
    if (!baseItem || !selectedUbicacion) return ""
    
    let descripcion = baseItem.descripcion || ""
    
    // Agregar atributos si existen
    const atributos = generarAtributosSubtexto(baseItem)
    if (atributos) {
      descripcion += ` - ${atributos}`
    }
    
    // Agregar destino
    if (selectedUbicacion?.label) {
      descripcion += ` / ${selectedUbicacion.label}`
    }
    
    return descripcion
  }, [baseItem, selectedUbicacion, generarAtributosSubtexto])

  // 🔥 OPTIMIZADO: Función auxiliar para agregar servicios evitando duplicados (SIN MÚLTIPLES setState)
  const agregarServiciosSinDuplicar = useCallback((servicios, idUbicacion, proveedorIdBuscador = null) => {
    if (bloqueado) {
      setError("El paquete está cerrado. Debe abrirse para editarlo.")
      return []
    }
    
    // Siempre usar proveedor genérico (id=2) al agregar
    const idEntidadPorDefecto = 2
    
    const serviciosNuevos = []
    const cantidadesActualizar = {}
    const entidadesActualizar = {}
    let nuevosIngredientes = []
    
    setIngredientes((prevIngredientes) => {
      nuevosIngredientes = [...prevIngredientes]
      
      servicios.forEach((servicio) => {
        const existeServicio = nuevosIngredientes.find(item => item.idItem === servicio.idItem)
        
        if (existeServicio) {
          // Ya existe: incrementar cantidad
          const key = `${servicio.idItem}-${idUbicacion}`
          cantidadesActualizar[key] = 'increment'
        } else {
          // Nuevo servicio: agregar con proveedor genérico
          const servicioConProveedor = { ...servicio, idEntidad: idEntidadPorDefecto }
          nuevosIngredientes.push(servicioConProveedor)
          serviciosNuevos.push(servicioConProveedor)
          
          const key = `${servicio.idItem}-${idUbicacion}`
          cantidadesActualizar[key] = "1"
          entidadesActualizar[servicio.idItem] = idEntidadPorDefecto
          
          // NO cargar costos automáticamente
          // Se cargarán cuando el usuario cambie el proveedor
        }
      })
      
      return nuevosIngredientes
    })
    
    // Actualizar cantidades y entidades
    if (Object.keys(cantidadesActualizar).length > 0) {
      setCantidades((prev) => {
        const updated = { ...prev }
        Object.entries(cantidadesActualizar).forEach(([key, value]) => {
          if (value === 'increment') {
            const cantidadActual = Number.parseFloat(updated[key] || 0)
            updated[key] = (cantidadActual + 1).toString()
          } else {
            updated[key] = value
          }
        })
        return updated
      })
    }
    
    if (Object.keys(entidadesActualizar).length > 0) {
      setEntidadesServicios((prev) => ({ ...prev, ...entidadesActualizar }))
    }
    
    return serviciosNuevos
  }, [bloqueado])

  // ✅ OPTIMIZADO: handleFilterSearch envuelto en useCallback para evitar recreación constante
  const handleFilterSearch = useCallback(async ({ ubicacionId, itemId, precio, soloStockBajo, mostrarUltimosCreados, proveedorId }) => {
    if (!itemId) return
    
    if (!selectedUbicacion) {
      setError("Debe seleccionar un destino primero")
      return
    }
    
    try {
      setLoading(true)
      await getItemAtributoNoEliminados()
      
      // Buscar el item directamente SIN verificar ItemUbicacion
      const searchFunction = !baseItem 
        ? getItemAndItemUbicacion 
        : getItemAndItemUbicacionServicios
      
      // Buscar SIN filtrar por ubicación (buscar en TODAS las ubicaciones)
      const response = await searchFunction(
        page,
        100,
        null, // Sin ubicación para buscar todos
        itemId,
        precio,
        soloStockBajo,
        mostrarUltimosCreados,
      )
      
      const items = response?.data || []
      
      if (!baseItem && items.length > 0) {
        // GRUPO: Establecer como base
        const grupoItem = items[0]
        
        setBaseItem(grupoItem)
        if (onBaseItemChange) {
          onBaseItemChange(grupoItem)
        }
        
        // Intentar cargar precio/stock SI YA EXISTE ItemUbicacion
        await cargarPrecioPaquete(grupoItem.idItem, selectedUbicacion.id)
        await cargarStockGrupo(grupoItem.idItem, selectedUbicacion.id)
        
        // Intentar cargar BOM existente
        try {
          const ingredientesAsociados = await getBomByIdItem(grupoItem.idItem, selectedUbicacion.id)
          
          if (ingredientesAsociados.length > 0) {
            const nuevosIngredientes = ingredientesAsociados.map((bom) => ({
              ...bom.materiaPrima,
              idItem: bom.idMateriaPrima,
              cantidad: bom.cantidad,
              idEntidad: bom.idEntidad || 2
            }))
            setIngredientes(nuevosIngredientes)
            
            const cantidadesMap = {}
            const entidadesMap = {}
            const idsEntidadesUnicas = new Set()
            ingredientesAsociados.forEach((bom) => {
              cantidadesMap[`${bom.idMateriaPrima}-${selectedUbicacion.id}`] = bom.cantidad.toString()
              if (bom.idEntidad) {
                entidadesMap[bom.idMateriaPrima] = bom.idEntidad
                idsEntidadesUnicas.add(bom.idEntidad)
              }
            })
            setCantidades(cantidadesMap)
            setEntidadesServicios(entidadesMap)
            
            // Cargar proveedores únicos en proveedoresDisponibles
            const idsACargar = Array.from(idsEntidadesUnicas).filter(id => id !== 2)
            if (idsACargar.length > 0) {
              const proveedoresCargados = await Promise.all(
                idsACargar.map(id => getEntidadByID(id).catch(() => null))
              )
              const proveedoresValidos = proveedoresCargados.filter(p => p !== null)
              if (proveedoresValidos.length > 0) {
                setProveedoresDisponibles(prev => [...prev, ...proveedoresValidos])
              }
            }
            
            setRecetaExistente(true)

            // Cargar fechas y noches de la BOM existente
            const primerBom = ingredientesAsociados[0]
            if (primerBom.fechaInicio) {
              setFechaInicio(new Date(primerBom.fechaInicio))
            }
            if (primerBom.fechaFin) {
              const fechaFinCargada = new Date(primerBom.fechaFin)
              setFechaFin(fechaFinCargada)
              if (primerBom.fechaInicio) {
                const fechaIniciaCargada = new Date(primerBom.fechaInicio)
                const diferencia = Math.ceil((fechaFinCargada - fechaIniciaCargada) / (1000 * 60 * 60 * 24))
                setDiasDuracion(diferencia > 0 ? diferencia.toString() : "")
              }
            }
            if (primerBom.noches) {
              setNoches(primerBom.noches.toString())
            }
          }
        } catch (bomError) {
          // No hay BOM, es normal
        }
          
          // Agregar items adicionales si los hay (SOLO SERVICIOS, NO GRUPOS)
          if (items.length > 1) {
            const serviciosAdicionales = items.slice(1).filter(item => item.esServicio === true)
            if (serviciosAdicionales.length > 0) {
              agregarServiciosSinDuplicar(serviciosAdicionales, selectedUbicacion.id, null)
            }
          }
        } else {
          // SERVICIOS: Agregar directamente
          if (items.length > 0) {
            agregarServiciosSinDuplicar(items, selectedUbicacion.id, null)
          }
        }
      } catch (error) {
        console.error("Error fetching inventory data:", error)
        setError("Error al buscar items")
      } finally {
        setLoading(false)
      }
    }, [
      selectedUbicacion, baseItem, page,
      getItemAtributoNoEliminados,
      getItemAndItemUbicacion, getItemAndItemUbicacionServicios,
      getBomByIdItem,
      cargarPrecioPaquete, cargarStockGrupo,
      agregarServiciosSinDuplicar, getEntidadByID,
      proveedorGenerico, proveedoresDisponibles, onBaseItemChange
    ])

  const handleRemoveIngredient = (key) => {
    if (bloqueado) {
      setError("El paquete está cerrado. Debe abrirse para editarlo.")
      return
    }
    setIngredientes((prev) => prev.filter((item) => `${item.idItem}-${selectedUbicacion?.id || 1}` !== key))
    setCantidades((prev) => {
      const updated = { ...prev }
      delete updated[key]
      return updated
    })
    setPreciosCostos((prev) => {
      const updated = { ...prev }
      delete updated[key]
      return updated
    })
  }

  const handleRemoveBaseItem = () => {
    if (bloqueado) {
      setError("El paquete está cerrado. Debe abrirse para editarlo.")
      return
    }
    setBaseItem(null)
    setIngredientes([])
    setCantidades({})
    setRecetaExistente(false)
    setPreciosCostos({})
    setPrecioFinalPaquete(0)
    setPrecioFinalPaqueteDisplay("")
    setPrecioFinalEditado(false)
    setCostosEditados({})
    setStockEditado(0)
    setStockEditadoDisplay("")
    setStockEditadoFlag(false)
    setEntidadesServicios({})
    setResetFiltrosKey((k) => k + 1)
    if (onBaseItemChange) {
      onBaseItemChange(null)
    }
    setFechaInicio(new Date())
    setFechaFin(null)
    setDiasDuracion("")
    setNoches("")
  }
  
  const handleUbicacionChange = (newUbicacion) => {
    if (bloqueado) {
      setError("El paquete está cerrado. Debe abrirse para editarlo.")
      return
    }
    setSelectedUbicacion(newUbicacion)
  }

  

  const onSearch = useCallback((filters) => {
    setSearchFilters(filters)
    handleFilterSearch(filters)
  }, [handleFilterSearch])

  const crearReceta = async () => {
    if (bloqueado) {
      setError("El paquete está cerrado. Debe abrirse para editarlo.")
      return
    }
    if (!baseItem) {
      setError("Debe seleccionar un grupo base para el servicio")
      return
    }

    if (ingredientes.length === 0) {
      setError("Debe agregar al menos un servicio")
      return
    }

    try {
      setLoading(true)
      
      // NUEVO: Verificar y crear ItemUbicacion/ListaDeMontos faltantes
      console.log("Verificando asociaciones faltantes antes de guardar...")
      
      // 1. Verificar el GRUPO (baseItem)
      try {
        const itemUbicaciones = await getItemUbicacionFilterByUbicacion(baseItem.idItem, selectedUbicacion.id)
        
        if (!itemUbicaciones || itemUbicaciones.length === 0) {
          console.log(`Creando ItemUbicacion para grupo ${baseItem.idItem} en ubicación ${selectedUbicacion.id}`)
          await postItemUbicacion(baseItem.idItem, selectedUbicacion.id, 0, 0) // stock=0, stockMinimo=0
        }
        
        // Verificar ListaDeMontos para el grupo (idEntidad=1 para cliente genérico)
        try {
          const montoExistente = await getListaMontosByIdItemIdEntidad(1, baseItem.idItem, selectedUbicacion.id)
          
          if (!montoExistente || montoExistente.monto === undefined) {
            console.log(`Creando ListaDeMontos para grupo ${baseItem.idItem}, ubicación ${selectedUbicacion.id}, idEntidad=1`)
            await updateListaDeMonto({
              idItem: baseItem.idItem,
              idUbicacion: selectedUbicacion.id,
              idEntidad: 1,
              monto: precioFinalPaquete || 0,
            })
          }
        } catch (error) {
          // No existe, crear
          console.log(`Creando ListaDeMontos para grupo ${baseItem.idItem}, ubicación ${selectedUbicacion.id}, idEntidad=1`)
          await updateListaDeMonto({
            idItem: baseItem.idItem,
            idUbicacion: selectedUbicacion.id,
            idEntidad: 1,
            monto: precioFinalPaquete || 0,
          })
        }
      } catch (error) {
        console.error(`Error verificando/creando asociación para grupo ${baseItem.idItem}:`, error)
      }
      
      // 2. Verificar cada SERVICIO
      for (const servicio of ingredientes) {
        try {
          const itemUbicaciones = await getItemUbicacionFilterByUbicacion(servicio.idItem, selectedUbicacion.id)
          
          if (!itemUbicaciones || itemUbicaciones.length === 0) {
            console.log(`Creando ItemUbicacion para servicio ${servicio.idItem} en ubicación ${selectedUbicacion.id}`)
            await postItemUbicacion(servicio.idItem, selectedUbicacion.id, 0, 0)
          }
          
          const idEntidad = servicio.idEntidad || entidadesServicios[servicio.idItem] || 2
          const key = `${servicio.idItem}-${selectedUbicacion.id}`
          const costoEditado = costosEditados[key]
          const costoActual = costoEditado !== undefined && costoEditado !== "" 
            ? parseFloat(costoEditado) 
            : (preciosCostos[key]?.costo || 0)
          
          // Verificar ListaDeMontos para el servicio
          try {
            const montoExistente = await getListaMontosByIdItemIdEntidad(idEntidad, servicio.idItem, selectedUbicacion.id)
            
            if (!montoExistente || montoExistente.monto === undefined) {
              console.log(`Creando ListaDeMontos para servicio ${servicio.idItem}, ubicación ${selectedUbicacion.id}, idEntidad=${idEntidad}`)
              await updateListaDeMonto({
                idItem: servicio.idItem,
                idUbicacion: selectedUbicacion.id,
                idEntidad: idEntidad,
                monto: costoActual,
              })
            }
          } catch (error) {
            // No existe, crear
            console.log(`Creando ListaDeMontos para servicio ${servicio.idItem}, ubicación ${selectedUbicacion.id}, idEntidad=${idEntidad}`)
            await updateListaDeMonto({
              idItem: servicio.idItem,
              idUbicacion: selectedUbicacion.id,
              idEntidad: idEntidad,
              monto: costoActual,
            })
          }
        } catch (error) {
          console.error(`Error verificando/creando asociación para servicio ${servicio.idItem}:`, error)
        }
      }
      
      console.log("Asociaciones verificadas/creadas correctamente")
      
      // 1. Preparar ingredientes con cantidades, montos e idEntidad
      // //console.log(`\n📋 [crearReceta] Preparando ingredientes para guardar en BOM:`)
      // //console.log(`   - Total ingredientes: ${ingredientes.length}`)
      
      const ingredientesConCantidad = ingredientes.map((item) => {
        const key = `${item.idItem}-${selectedUbicacion?.id || 1}`
        const cantidad = ocultarCantidadYCostoXCantidad ? 1 : (parseFloat(cantidades[key]) || 0)
        
        // ✅ Usar costo editado si existe, sino el costo de preciosCostos
        const costoEditado = costosEditados[key]
        const costoUnitario = costoEditado !== undefined && costoEditado !== "" 
          ? parseFloat(costoEditado) 
          : (preciosCostos[key]?.costo || 0)
        
        const montoTotal = costoUnitario * cantidad
        const idEntidadServicio = item.idEntidad || entidadesServicios[item.idItem] || preciosCostos[key]?.idEntidadCosto || 2
        
        // //console.log(`\n   📦 Servicio ID: ${item.idItem} (${item.descripcion || 'Sin descripción'})`)
        // //console.log(`      - Key: ${key}`)
        // //console.log(`      - Cantidad: ${cantidad}`)
        // //console.log(`      - Costo unitario: $${costoUnitario} ${costoEditado !== undefined ? '(EDITADO manualmente)' : '(desde preciosCostos)'}`)
        // //console.log(`      - Monto total (costo × cantidad): $${montoTotal}`)
        // //console.log(`      - ID Entidad (proveedor): ${idEntidadServicio}`)
        // //console.log(`      - Origen del costo: ${preciosCostos[key]?.fromBOM ? 'BOM (histórico)' : 'ListaDeMontos (actual)'}`)
        
        return {
          ...item,
          cantidad: cantidad,
          monto: montoTotal, // ✅ Costo total = costo unitario × cantidad
          idEntidad: idEntidadServicio, // ✅ ID del proveedor
        }
      })
      
      // //console.log(`\n✅ [crearReceta] Ingredientes preparados. Total a guardar en BOM: ${ingredientesConCantidad.length}`)

      // 2. Crear/Actualizar la receta (BOM)
      // Validar que la Fecha Inicio esté seleccionada
      if (!fechaInicio) {
        setError("La Fecha Inicio es obligatoria para crear el paquete")
        setLoading(false)
        return
      }
      
      const fechaInicioISO = fechaInicio.toISOString()
      const fechaFinISO = fechaFin ? fechaFin.toISOString() : null
      
      const nochesValue = noches && noches !== "" ? parseInt(noches, 10) : null
      
      // Obtener idPaquete si existe (para edición directa)
      const idPaqueteEdicion = modoEdicion && paqueteInicial?.idPaquete ? paqueteInicial.idPaquete : null
      
      // ✅ NUEVO: Construir descripción del paquete para gastos automáticos
      const descripcionPaquete = construirDescripcionPaquete()
      
      if (recetaExistente) {
        const response = await crearRecetaApi(
          baseItem.idItem,
          ingredientesConCantidad,
          true,
          selectedUbicacion.id,
          fechaInicioISO,
          fechaFinISO,
          nochesValue,
          idPaqueteEdicion, // Pasar idPaquete si existe
          descripcionPaquete, // ✅ NUEVO: Descripción para gastos automáticos
        )
        setSuccess(true)
        setError(null)
        //console.log("✅ Paquete actualizado", response?.idPaquete ? `(ID: ${response.idPaquete})` : "")
      } else {
        const response = await crearRecetaApi(
          baseItem.idItem,
          ingredientesConCantidad,
          false,
          selectedUbicacion.id,
          fechaInicioISO,
          fechaFinISO,
          nochesValue,
          idPaqueteEdicion, // Pasar idPaquete si existe
          descripcionPaquete, // ✅ NUEVO: Descripción para gastos automáticos
        )
        setSuccess(true)
        setError(null)
        //console.log("✅ Paquete creado", response?.idPaquete ? `(ID: ${response.idPaquete})` : "")
      }

      // 3. Actualizar stock del grupo (ItemUbicacion) si fue editado
      if (stockEditadoFlag && baseItem && selectedUbicacion) {
        const stockFinal = typeof stockEditado === "number" ? stockEditado : (stockEditado === "" ? 0 : Number.parseFloat(stockEditado) || 0)
        await updateItemUbicaciones(baseItem.idItem, [
          {
          idUbicacion: selectedUbicacion.id,
          inventario: stockFinal,
            stockMinimo: 0,
          },
        ])
        //console.log(`✅ Stock del grupo actualizado: ${stockFinal}`)
        setStockEditadoFlag(false)
      }

      // 4. Actualizar precio del paquete (ListaDeMontos del grupo) si fue editado
      if (precioFinalEditado && baseItem && selectedUbicacion) {
        const precioFinal = typeof precioFinalPaquete === "number" ? precioFinalPaquete : (precioFinalPaquete === "" ? 0 : Number.parseFloat(precioFinalPaquete) || 0)
        const response = await updateListaDeMonto({
          idItem: baseItem.idItem,
          idUbicacion: selectedUbicacion.id,
          idEntidad: 1,
          monto: precioFinal,
        })
        //console.log(`✅ Precio del paquete actualizado: $${precioFinal}`)
        
        // 🔄 SINCRONIZACIÓN: Verificar si el plan de cuotas fue recalculado
        if (response?.planRecalculado) {
          //console.log(`⚠️ Plan de cuotas recalculado automáticamente - Versión ${response.planRecalculado.version}`)
          //console.log(`   Precio anterior: $${response.planRecalculado.precioAnterior}`)
          //console.log(`   Precio nuevo: $${response.planRecalculado.precioNuevo}`)
          
          // Mostrar alerta al usuario
          setTimeout(() => {
            alert(
              `⚠️ IMPORTANTE: El plan de cuotas fue recalculado automáticamente.\n\n` +
              `Precio anterior: $${response.planRecalculado.precioAnterior}\n` +
              `Precio nuevo: $${response.planRecalculado.precioNuevo}\n\n` +
              `Debes aplicar manualmente el plan actualizado a los pasajeros desde la sección "Editar Pasajeros > Cuotas".`
            )
          }, 1000)
        }
        
        setPrecioFinalEditado(false)
      }

      // 5. Actualizar costos de servicios (ListaDeMontos de servicios) si fueron editados
      if (Object.keys(costosEditados).length > 0) {
        for (const [key, valor] of Object.entries(costosEditados)) {
          const [idItem, idUbicacion] = key.split("-").map(Number)
          const costoFinal = valor === "" ? 0 : Number.parseFloat(valor) || 0
          await updateListaDeMonto({
            idItem,
            idUbicacion,
            idEntidad: 2,
            monto: costoFinal,
          })
          // //console.log(`✅ Costo del servicio ${idItem} actualizado: $${costoFinal}`)
        }
        setCostosEditados({})
      }

      // 6. Actualizar estado local con los valores guardados
      const nuevosPreciosCostos = { ...preciosCostos }
      for (const [key, valor] of Object.entries(costosEditados)) {
        if (nuevosPreciosCostos[key]) {
          nuevosPreciosCostos[key].costo = valor === "" ? 0 : Number.parseFloat(valor) || 0
        }
      }
      setPreciosCostos(nuevosPreciosCostos)

      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
      //console.log("🎉 Paquete actualizado completamente")
      
      // 🔧 Llamar callback si existe (modo modal)
      if (onGuardadoExitoso) {
        setTimeout(() => onGuardadoExitoso(), 1000) // Esperar un poco para que se vea el success
      }
    } catch (error) {
      console.error("Error creando o actualizando receta:", error)
      setError("Error al crear/actualizar el paquete")
    } finally {
      setLoading(false)
    }
  }

  // 🔧 Abrir/Cerrar paquete manualmente
  const toggleCerradoPaquete = async () => {
    if (!modoEdicion || !paqueteInicial?.idPaquete) {
      setError("No se pudo identificar el paquete para cambiar su estado.")
      return
    }

    const nuevoEstado = !paqueteCerrado

    try {
      setLoadingCerrado(true)
      setError(null)

      await setCerradoPaqueteApi(paqueteInicial.idPaquete, nuevoEstado)
      setPaqueteCerrado(nuevoEstado)

      // Si se cerró, cerrar el modal automáticamente
      if (nuevoEstado === true) {
        if (onCancelar) onCancelar({ forceRefetch: true })
      }
    } catch (e) {
      setError(e?.response?.data?.message || "No se pudo actualizar el estado del paquete.")
    } finally {
      setLoadingCerrado(false)
    }
  }

  const calcularTotalIngredientes = () => {
    return ingredientes.reduce((total, item) => {
      const key = `${item.idItem}-${selectedUbicacion?.id || 1}`
      const cantidad = ocultarCantidadYCostoXCantidad ? 1 : Number.parseFloat(cantidades[key] || 0)
      return total + (Number.isFinite(cantidad) ? cantidad : 0)
    }, 0)
  }

  const validarCantidades = () => {
    if (ocultarCantidadYCostoXCantidad) return true
    return ingredientes.every((item) => {
      const key = `${item.idItem}-${selectedUbicacion?.id || 1}`
      const cantidad = Number.parseFloat(cantidades[key] || 0)
      return cantidad > 0
    })
  }

  // 🔧 VALIDACIÓN DE FECHAS
  const validarFechas = () => {
    if (!fechaInicio) return false
    if (fechaFin && fechaFin < fechaInicio) return false
    return true
  }

  // 🔧 VALIDACIÓN GENERAL
  const validarFormulario = () => {
    return baseItem && ingredientes.length > 0 && validarCantidades() && validarFechas()
  }

  // 🔧 FUNCIÓN PARA FORMATEAR MONEDA ARGENTINA
  const formatearMoneda = (valor) => {
    if (typeof valor !== 'number' || isNaN(valor)) return '$0'
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(valor)
  }

  // 🔧 FUNCIÓN PARA GENERAR COLOR GRADIENTE BASADO EN PORCENTAJE DE GANANCIA
  const generarColorGradiente = (porcentaje) => {
    // Normalizar el porcentaje entre 0 y 100
    const porcentajeNormalizado = Math.max(0, Math.min(100, porcentaje))
    
    // Definir colores del gradiente
    const colores = {
      muyBajo: '#ff4444',    // Rojo - Pérdida o muy baja ganancia
      bajo: '#ff8800',       // Naranja - Ganancia baja
      medio: '#ffcc00',      // Amarillo - Ganancia media
      bueno: '#88cc00',      // Verde claro - Buena ganancia
      excelente: '#44aa44'   // Verde - Excelente ganancia
    }
    
    // Definir rangos de porcentaje
    if (porcentajeNormalizado < 0) return colores.muyBajo
    if (porcentajeNormalizado < 10) return colores.bajo
    if (porcentajeNormalizado < 20) return colores.medio
    if (porcentajeNormalizado < 30) return colores.bueno
    return colores.excelente
  }
  
  // Analíticos dinámicos
  const calcularCostoTotal = () => {
    return ingredientes.reduce((total, item) => {
      const key = `${item.idItem}-${selectedUbicacion?.id || 1}`
      const cantidad = ocultarCantidadYCostoXCantidad ? 1 : Number.parseFloat(cantidades[key] || 0)
      const costoEditado = costosEditados[key]
      const costoUnitario = costoEditado === "" ? 0 : Number.parseFloat(costoEditado || preciosCostos[key]?.costo || 0)
      const qty = Number.isFinite(cantidad) ? cantidad : 0
      return total + qty * costoUnitario
    }, 0)
  }
  
  const calcularCostoTotalServicios = () => {
    return ingredientes.reduce((total, item) => {
      const key = `${item.idItem}-${selectedUbicacion?.id || 1}`
      const cantidad = ocultarCantidadYCostoXCantidad ? 1 : Number.parseFloat(cantidades[key] || 0)
      const costoEditado = costosEditados[key]
      const costoUnitario = costoEditado === "" ? 0 : Number.parseFloat(costoEditado || preciosCostos[key]?.costo || 0)
      const qty = Number.isFinite(cantidad) ? cantidad : 0
      return total + qty * costoUnitario
    }, 0)
  }
  
  const calcularGanancia = () => {
    // Ganancia por paquete considerando pasajeros: Ingreso total - Costo total servicios
    const ingresoTotal = calcularIngresoTotal()
    const costoServicios = calcularCostoTotalServicios()
    return ingresoTotal - costoServicios
  }
  
  const calcularPorcentajeGanancia = () => {
    const costoTotal = calcularCostoTotal()
    if (costoTotal === 0) return 0
    return ((precioFinalPaquete - costoTotal) / costoTotal) * 100
  }
  
  const calcularMargenGanancia = () => {
    // % Ganancia sobre el ingreso total (precio*pasajeros)
    const ingresoTotal = calcularIngresoTotal()
    if (ingresoTotal === 0) return 0
    const ganancia = calcularGanancia()
    return (ganancia / ingresoTotal) * 100
  }

  const calcularVentasPosibles = () => {
    return typeof stockEditado === "number" ? stockEditado : (stockEditado === "" ? 0 : Number.parseFloat(stockEditado) || 0)
  }

  const calcularIngresoTotal = () => {
    const precioPaquete = typeof precioFinalPaquete === "number" ? precioFinalPaquete : (precioFinalPaquete === "" ? 0 : Number.parseFloat(precioFinalPaquete) || 0)
    return precioPaquete * calcularVentasPosibles()
  }

  const calcularCostoTotalInventario = () => {
    return calcularCostoTotal() * calcularVentasPosibles()
  }

  const calcularGananciaTotalPotencial = () => {
    return calcularIngresoTotal() - calcularCostoTotalInventario()
  }

  const calcularPuntoEquilibrio = () => {
    const gananciaUnitaria = calcularGanancia()
    if (gananciaUnitaria <= 0) return 0
    return Math.ceil(calcularCostoTotal() / gananciaUnitaria)
  }

  const calcularROI = () => {
    const costoTotal = calcularCostoTotalInventario()
    if (costoTotal === 0) return 0
    return (calcularGananciaTotalPotencial() / costoTotal) * 100
  }

  const obtenerDatosComparacion = () => {
    return [
      {
        name: "Precio Venta",
        value: precioFinalPaquete,
        fill: theme.palette.success.main,
      },
      {
        name: "Costo Total",
        value: calcularCostoTotal(),
        fill: theme.palette.warning.main,
      },
      {
        name: "Ganancia",
        value: calcularGanancia(),
        fill: calcularGanancia() >= 0 ? theme.palette.info.main : theme.palette.error.main,
      },
    ]
  }

  const obtenerDatosDistribucionCostos = () => {
    return ingredientes.map((item) => {
      const key = `${item.idItem}-${selectedUbicacion?.id || 1}`
      const cantidad = ocultarCantidadYCostoXCantidad ? 1 : Number.parseFloat(cantidades[key] || 0)
      const costoUnitario = Number.parseFloat(costosEditados[key] || preciosCostos[key]?.costo || 0)
      const qty = Number.isFinite(cantidad) ? cantidad : 0
      const subtotal = qty * costoUnitario

      return {
        name: item.descripcion.length > 20 ? item.descripcion.substring(0, 20) + "..." : item.descripcion,
        value: subtotal,
        porcentaje: calcularCostoTotal() > 0 ? ((subtotal / calcularCostoTotal()) * 100).toFixed(1) : 0,
      }
    })
  }

  const obtenerDatosProyeccionVentas = () => {
    const ventasPosibles = calcularVentasPosibles()
    const data = []

    for (let i = 0; i <= Math.min(ventasPosibles, 10); i++) {
      const unidades = Math.ceil((ventasPosibles / 10) * i)
      data.push({
        unidades,
        ingresos: precioFinalPaquete * unidades,
        costos: calcularCostoTotal() * unidades,
        ganancia: calcularGanancia() * unidades,
      })
    }

    return data
  }

  const obtenerDatosMargenGanancia = () => {
    const margen = calcularMargenGanancia()
    return [
      {
        name: "Margen",
        value: Math.max(0, Math.min(100, margen)),
        fill:
          margen >= 30
            ? theme.palette.success.main
            : margen >= 15
              ? theme.palette.warning.main
              : theme.palette.error.main,
      },
    ]
  }

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff8042",
  ]

  return (
    <Box sx={{ 
      p: enModal ? 0.5 : 2, 
      backgroundColor: "#f8f9fa", 
      minHeight: enModal ? 0 : "100vh",
      overflowX: "hidden", // Evitar scroll horizontal en todo el componente
      maxWidth: "100%",
      display: enModal ? "flex" : "block",
      flexDirection: enModal ? "column" : "row",
      height: "auto",
      overflowY: enModal ? "visible" : "auto",
      flex: enModal ? "1 1 auto" : "0 0 auto",
    }}>

      {bloqueado && (
        <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>
          Este paquete está <strong>cerrado</strong>. No se puede modificar (servicios, fechas, costos, precio ni pasajeros).
          Para editarlo, usá el botón <strong>Abrir Paquete</strong>.
        </Alert>
      )}

      {/* Buscador - Con filtrado inteligente para recetas */}
      <Box sx={{ mb: enModal ? 1 : 2, width: "100%", maxWidth: "100%" }}>
        {mostrarBarraUnificada ? (
          <Paper 
            elevation={1} 
            sx={{ 
              p: 1, 
              borderRadius: 1.5,
              position: "relative",
              overflow: "visible",
              zIndex: 100,
              border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
              backgroundColor: alpha(theme.palette.grey[50], 0.3),
              width: "100%",
              maxWidth: "100%",
            }}
          >
            {/* FILA 1: Buscador arriba */}
            <Box sx={{ mb: 1 }}>
              <BuscadorItemsUbicaciones 
                onSearch={onSearch} 
                ubicacionBloqueada={modoEdicion ? false : ingredientes.length > 0}
                onUbicacionChange={handleUbicacionChange}
                labelBusqueda={!baseItem ? "Buscar GRUPO base (paquete)" : "Servicios"}
                modoPaquetes={true}
                grupoSeleccionado={baseItem !== null}
                vieneDe="paquetes"
                ubicacionInicial={ocultarDestino ? selectedUbicacion : null}
                modoEdicion={ocultarDestino}
                ocultarUbicacion={ocultarDestino}
                sinPaper={true}
                unaLinea={true}
                resetKey={resetFiltrosKey}
              />
            </Box>

            {/* FILA 2: Filtros abajo */}
            <Box
              sx={{
                display: "grid",
                columnGap: 0.75,
                rowGap: 0.75,
                alignItems: "center",
                width: "100%",
                maxWidth: "100%",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(7, 1fr) auto",
                },
              }}
            >

              <TextField
                variant="outlined"
                size="small"
                fullWidth
                type="text"
                label="Precio"
                InputLabelProps={{ shrink: true }}
                disabled={bloqueado || !baseItem}
                value={precioFinalPaqueteDisplay}
                onChange={(e) => {
                  const inputValue = e.target.value
                  const { locale, decimalSeparator } = localeConfig

                  if (inputValue === "") {
                    setPrecioFinalPaqueteDisplay("")
                    setPrecioFinalPaquete("")
                    setPrecioFinalEditado(true)
                    return
                  }

                  const parsedValue = parseLocalizedNumber(inputValue, locale)

                  if (parsedValue === null && inputValue.trim() !== "") {
                    return
                  }

                  let formattedValue = parsedValue !== null ? formatInputWithSeparators(parsedValue, locale) : ""

                  const hasDecimalSeparator = inputValue.includes(decimalSeparator)
                  const endsWithDecimal = inputValue.endsWith(decimalSeparator)
                  const [, fractionalPartOriginal] = inputValue.split(decimalSeparator)

                  if (endsWithDecimal && !formattedValue.endsWith(decimalSeparator)) {
                    formattedValue += decimalSeparator
                  } else if (hasDecimalSeparator && fractionalPartOriginal !== undefined) {
                    const cleanedFraction = fractionalPartOriginal.replace(/\D/g, "").slice(0, 6)
                    const integerPart = formattedValue.split(decimalSeparator)[0]
                    formattedValue = `${integerPart}${decimalSeparator}${cleanedFraction}`
                  }

                  setPrecioFinalPaqueteDisplay(formattedValue)
                  setPrecioFinalPaquete(parsedValue !== null ? parsedValue : "")
                  setPrecioFinalEditado(true)

                  requestAnimationFrame(() => {
                    const input = e.target
                    if (input) {
                      input.setSelectionRange(formattedValue.length, formattedValue.length)
                    }
                  })
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon sx={{ fontSize: 18 }} color="success" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    fontSize: "0.875rem",
                    backgroundColor: precioFinalEditado ? alpha(theme.palette.success.main, 0.08) : alpha(theme.palette.success.main, 0.03),
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                }}
              />

              <TextField
                variant="outlined"
                size="small"
                fullWidth
                type="text"
                label="Pasajeros"
                InputLabelProps={{ shrink: true }}
                disabled={bloqueado || !baseItem}
                value={stockEditadoDisplay}
                onChange={(e) => {
                  const inputValue = e.target.value
                  const { locale, decimalSeparator } = localeConfig

                  if (inputValue === "") {
                    setStockEditadoDisplay("")
                    setStockEditado("")
                    setStockEditadoFlag(true)
                    return
                  }

                  const parsedValue = parseLocalizedNumber(inputValue, locale)

                  if (parsedValue === null && inputValue.trim() !== "") {
                    return
                  }

                  let formattedValue = parsedValue !== null ? formatInputWithSeparators(parsedValue, locale) : ""

                  const hasDecimalSeparator = inputValue.includes(decimalSeparator)
                  const endsWithDecimal = inputValue.endsWith(decimalSeparator)
                  const [, fractionalPartOriginal] = inputValue.split(decimalSeparator)

                  if (endsWithDecimal && !formattedValue.endsWith(decimalSeparator)) {
                    formattedValue += decimalSeparator
                  } else if (hasDecimalSeparator && fractionalPartOriginal !== undefined) {
                    const cleanedFraction = fractionalPartOriginal.replace(/\D/g, "").slice(0, 6)
                    const integerPart = formattedValue.split(decimalSeparator)[0]
                    formattedValue = `${integerPart}${decimalSeparator}${cleanedFraction}`
                  }

                  setStockEditadoDisplay(formattedValue)
                  setStockEditado(parsedValue !== null ? parsedValue : "")
                  setStockEditadoFlag(true)

                  requestAnimationFrame(() => {
                    const input = e.target
                    if (input) {
                      input.setSelectionRange(formattedValue.length, formattedValue.length)
                    }
                  })
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <GroupsIcon sx={{ fontSize: 18 }} color="primary" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    fontSize: "0.875rem",
                    backgroundColor: stockEditadoFlag ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.primary.main, 0.03),
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                }}
              />

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={dayjs(fechaInicio)}
                  onChange={(newValue) => {
                    const nuevaFechaInicio = newValue ? newValue.toDate() : new Date()
                    setFechaInicio(nuevaFechaInicio)
                    if (fechaFin) {
                      const diferencia = Math.ceil((fechaFin - nuevaFechaInicio) / (1000 * 60 * 60 * 24))
                      setDiasDuracion(diferencia > 0 ? diferencia.toString() : "")
                    }
                    if (diasDuracion && diasDuracion !== "") {
                      const dias = parseInt(diasDuracion, 10)
                      if (!isNaN(dias) && dias > 0) {
                        const nuevaFechaFin = new Date(nuevaFechaInicio)
                        nuevaFechaFin.setDate(nuevaFechaFin.getDate() + dias)
                        setFechaFin(nuevaFechaFin)
                      }
                    }
                  }}
                  format={dateFormat}
                  disabled={bloqueado || !baseItem}
                  slots={{
                    openPickerIcon: CalendarTodayIcon,
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      required: true,
                      error: !fechaInicio,
                      helperText: "",
                      label: "Fecha Inicio",
                      InputLabelProps: { required: false, shrink: true },
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1,
                          fontSize: "0.875rem",
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.875rem",
                        },
                      },
                    },
                    openPickerIcon: {
                      sx: { fontSize: 18 },
                    },
                  }}
                />
              </LocalizationProvider>

              <TextField
                label="Duración"
                fullWidth
                size="small"
                type="number"
                InputLabelProps={{ shrink: true }}
                disabled={bloqueado || !baseItem}
                value={diasDuracion}
                onChange={(e) => {
                  const valor = e.target.value
                  setDiasDuracion(valor)
                  if (fechaInicio && valor !== "" && !isNaN(parseInt(valor, 10))) {
                    const dias = parseInt(valor, 10)
                    if (dias > 0) {
                      const nuevaFechaFin = new Date(fechaInicio)
                      nuevaFechaFin.setDate(nuevaFechaFin.getDate() + dias)
                      setFechaFin(nuevaFechaFin)
                    } else if (dias === 0) {
                      setFechaFin(null)
                    }
                  }
                }}
                inputProps={{ min: 0, step: 1 }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                }}
              />

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={fechaFin ? dayjs(fechaFin) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      const nuevaFechaFin = newValue.toDate()
                      setFechaFin(nuevaFechaFin)
                      if (fechaInicio) {
                        const diferencia = Math.ceil((nuevaFechaFin - fechaInicio) / (1000 * 60 * 60 * 24))
                        setDiasDuracion(diferencia > 0 ? diferencia.toString() : "")
                      }
                    } else {
                      setFechaFin(null)
                      setDiasDuracion("")
                    }
                  }}
                  format={dateFormat}
                  minDate={dayjs(fechaInicio)}
                  disabled={bloqueado || !baseItem}
                  slots={{
                    openPickerIcon: CalendarTodayIcon,
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      label: "Fecha Fin",
                      error: fechaFin && fechaFin < fechaInicio,
                      helperText: "",
                      InputLabelProps: { shrink: true },
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1,
                          fontSize: "0.875rem",
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.875rem",
                        },
                      },
                    },
                    openPickerIcon: {
                      sx: { fontSize: 18 },
                    },
                  }}
                />
              </LocalizationProvider>

                  <TextField
                    label="Noches"
                    fullWidth
                    size="small"
                    type="number"
                InputLabelProps={{ shrink: true }}
                disabled={bloqueado || !baseItem}
                    value={noches}
                onChange={(e) => setNoches(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
              />

              <Tooltip title="Limpiar filtros">
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleResetFiltrosBarra}
                  sx={{ 
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                    backgroundColor: alpha(theme.palette.error.main, 0.05),
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                >
                  <FilterAltOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        ) : (
          <BuscadorItemsUbicaciones 
            onSearch={onSearch} 
            ubicacionBloqueada={modoEdicion ? false : ingredientes.length > 0}
            onUbicacionChange={handleUbicacionChange}
            labelBusqueda={!baseItem ? "Buscar GRUPO base (paquete)" : "Servicios"}
            modoPaquetes={true}
            grupoSeleccionado={baseItem !== null}
            vieneDe="paquetes"
            ubicacionInicial={ocultarDestino ? selectedUbicacion : null}
            modoEdicion={ocultarDestino}
            ocultarUbicacion={ocultarDestino}
            
          />
        )}
      </Box>

      {/* Campos de fecha del paquete - Solo fuera de modal */}
      {!mostrarBarraUnificada && !enModal && baseItem && ingredientes.length > 0 && (
        <Card elevation={2} sx={{ mb: 2, mt: 2, borderRadius: 3, width: "100%", maxWidth: "100%" }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                Fechas del Paquete
              </Typography>
            </Box>
            
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", width: "100%", maxWidth: "100%" }}>
                {/* Fecha Inicio - Obligatoria */}
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Fecha Inicio
                  </Typography>
                  <DatePicker
                    value={dayjs(fechaInicio)}
                    onChange={(newValue) => {
                      const nuevaFechaInicio = newValue ? newValue.toDate() : new Date()
                      setFechaInicio(nuevaFechaInicio)
                      // Si hay fecha fin, recalcular días
                      if (fechaFin) {
                        const diferencia = Math.ceil((fechaFin - nuevaFechaInicio) / (1000 * 60 * 60 * 24))
                        setDiasDuracion(diferencia > 0 ? diferencia.toString() : "")
                      }
                      // Si hay días de duración, recalcular fecha fin
                      if (diasDuracion && diasDuracion !== "") {
                        const dias = parseInt(diasDuracion, 10)
                        if (!isNaN(dias) && dias > 0) {
                          const nuevaFechaFin = new Date(nuevaFechaInicio)
                          nuevaFechaFin.setDate(nuevaFechaFin.getDate() + dias)
                          setFechaFin(nuevaFechaFin)
                        }
                      }
                    }}
                    format={dateFormat}
                    slots={{
                      openPickerIcon: () => null,
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        required: true,
                        error: !fechaInicio,
                        helperText: !fechaInicio ? "La Fecha Inicio es obligatoria" : "",
                        InputLabelProps: { required: false },
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        },
                      },
                    }}
                  />
                </Box>

                {/* Campo de días de duración */}
                <Box sx={{ flex: { xs: 1, sm: 0.3 }, minWidth: 150 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Duración
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={diasDuracion}
                    onChange={(e) => {
                      const valor = e.target.value
                      setDiasDuracion(valor)
                      // Si hay fecha inicio y se ingresan días, calcular fecha fin
                      if (fechaInicio && valor !== "" && !isNaN(parseInt(valor, 10))) {
                        const dias = parseInt(valor, 10)
                        if (dias > 0) {
                          const nuevaFechaFin = new Date(fechaInicio)
                          nuevaFechaFin.setDate(nuevaFechaFin.getDate() + dias)
                          setFechaFin(nuevaFechaFin)
                        } else if (dias === 0) {
                          setFechaFin(null)
                        }
                      } else if (valor === "") {
                        // Si se borra el campo, no cambiar fecha fin
                      }
                    }}
                    
                    inputProps={{
                      min: 0,
                      step: 1,
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Calcula fecha fin automáticamente"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Box>

                {/* Fecha de fin - Opcional */}
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Fecha Fin (Opcional)
                  </Typography>
                  <DatePicker
                    value={fechaFin ? dayjs(fechaFin) : null}
                    onChange={(newValue) => {
                      if (newValue) {
                        const nuevaFechaFin = newValue.toDate()
                        setFechaFin(nuevaFechaFin)
                        // Calcular días de diferencia
                        if (fechaInicio) {
                          const diferencia = Math.ceil((nuevaFechaFin - fechaInicio) / (1000 * 60 * 60 * 24))
                          setDiasDuracion(diferencia > 0 ? diferencia.toString() : "")
                        }
                      } else {
                        setFechaFin(null)
                        setDiasDuracion("")
                      }
                    }}
                    format={dateFormat}
                    minDate={dayjs(fechaInicio)} // 🔧 VALIDACIÓN: No puede ser menor a fecha inicio
                    slots={{
                      openPickerIcon: () => null,
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        placeholder: "Seleccionar fecha fin",
                        error: fechaFin && fechaFin < fechaInicio, // 🔧 VALIDACIÓN VISUAL
                        helperText:
                          fechaFin && fechaFin < fechaInicio
                            ? "La fecha fin no puede ser menor a la Fecha Inicio"
                            : "Se calcula automáticamente con días",
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        },
                      },
                    }}
                  />
                </Box>

                {/* Campo de noches */}
                <Box sx={{ flex: { xs: 1, sm: 0.3 }, minWidth: 150 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Noches
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={noches}
                    onChange={(e) => {
                      const valor = e.target.value
                      setNoches(valor)
                    }}
                    inputProps={{
                      min: 0,
                      step: 1,
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Número de noches del viaje"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Box>
              </Box>
            </LocalizationProvider>
          </CardContent>
        </Card>
      )}

      {/* Contenido principal */}
      <Box sx={{ 
        display: "flex", 
        gap: enModal ? 1 : 2, 
        flexDirection: "column",
        width: "100%",
        maxWidth: "100%",
        flex: enModal ? "1 1 auto" : "0 0 auto",
        overflow: "visible", // ✅ Siempre visible para que Autocomplete se vea
        minHeight: 0,
        flexShrink: enModal ? 1 : 0,
      }}>
        {/* Item Base */}
        {(enModal && mostrarBarraUnificada) ? null : (
        <Card
          elevation={2}
          sx={{
            width: "100%",
            borderRadius: enModal ? 1.5 : 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
            maxWidth: "100%",
            overflow: "hidden",
            display: enModal && !baseItem ? "none" : "block",
            flex: enModal ? "0 0 auto" : "0 0 auto",
            p: 1,
            backgroundColor: alpha(theme.palette.grey[50], 0.5),
          }}
        >
          {!enModal && (
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                  <GroupsIcon />
                </Avatar>
              }
              title="Grupo"
              sx={{ pb: 1 }}
            />
          )}
          {!enModal && <Divider />}
          <CardContent sx={{ 
            p: enModal ? 0 : 1.5, 
            pt: enModal ? 0 : 1.5,
            pb: enModal ? 0 : 1.5,
            px: enModal ? 0 : 1.5,
            "&:last-child": { pb: enModal ? 0 : 1.5 },
            m: 0,
            backgroundColor: "transparent",
          }}>
            {baseItem ? (
              <Box sx={{ m: 0, p: 0 }}>
                {!enModal && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                      mb: 1.5,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.dark, mb: 0.5 }}>
                          {baseItem.descripcion}
                        </Typography>
                        {generarAtributosSubtexto(baseItem) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                            display: "block", 
                            mt: 0.5, 
                            fontStyle: "italic",
                              opacity: 0.8,
                            }}
                          >
                            {generarAtributosSubtexto(baseItem)}
                          </Typography>
                        )}
                      </Box>
                      <Tooltip title="Remover item base">
                        <IconButton
                          size="small"
                          onClick={handleRemoveBaseItem}
                          sx={{
                            color: theme.palette.error.main,
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.error.main, 0.2),
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                )}

                {/* Precio del paquete y Pasajeros - ahora en la barra superior */}
                {!mostrarBarraUnificada && (
                <Box sx={{ display: "flex", gap: enModal ? 1 : 1.5, p: 0, m: 0, mb: 0 }}>
                  {/* Precio del paquete */}
                  <Box sx={{ flex: 1, p: 0, m: 0 }}>
                    {!enModal && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Precio
                      </Typography>
                        <Tooltip title="Precio total que pagará el cliente por el paquete completo">
                          <InfoIcon fontSize="small" color="action" />
                        </Tooltip>
                      </Box>
                    )}
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="text"
                      label={enModal ? "Precio" : undefined}
                      value={precioFinalPaqueteDisplay}
                      onChange={(e) => {
                        const inputValue = e.target.value
                        const { locale, decimalSeparator } = localeConfig

                        if (inputValue === "") {
                          setPrecioFinalPaqueteDisplay("")
                          setPrecioFinalPaquete("")
                          setPrecioFinalEditado(true)
                          return
                        }

                        const parsedValue = parseLocalizedNumber(inputValue, locale)

                        if (parsedValue === null && inputValue.trim() !== "") {
                          return // Mantener valor anterior si no se puede parsear
                        }

                        let formattedValue = parsedValue !== null
                          ? formatInputWithSeparators(parsedValue, locale)
                          : ""

                        const hasDecimalSeparator = inputValue.includes(decimalSeparator)
                        const endsWithDecimal = inputValue.endsWith(decimalSeparator)
                        const [, fractionalPartOriginal] = inputValue.split(decimalSeparator)

                        // Manejo especial para separador decimal al final o durante entrada fraccional
                        if (endsWithDecimal && !formattedValue.endsWith(decimalSeparator)) {
                          formattedValue += decimalSeparator
                        } else if (hasDecimalSeparator && fractionalPartOriginal !== undefined) {
                          const cleanedFraction = fractionalPartOriginal.replace(/\D/g, "").slice(0, 6)
                          const integerPart = formattedValue.split(decimalSeparator)[0]
                          formattedValue = `${integerPart}${decimalSeparator}${cleanedFraction}`
                        }

                        setPrecioFinalPaqueteDisplay(formattedValue)
                        setPrecioFinalPaquete(parsedValue !== null ? parsedValue : "")
                        setPrecioFinalEditado(true)

                        // Mantener cursor al final
                        requestAnimationFrame(() => {
                          const input = e.target
                          if (input) {
                            input.setSelectionRange(formattedValue.length, formattedValue.length)
                          }
                        })
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoneyIcon color="success" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        m: 0,
                        mb: 0,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1.5,
                          backgroundColor: precioFinalEditado 
                            ? alpha(theme.palette.success.main, 0.15)
                            : alpha(theme.palette.success.main, 0.05),
                          fontWeight: 600,
                          fontSize: enModal ? "1.25rem" : "1.5rem",
                          height: enModal ? "48px" : "40px",
                          margin: 0,
                          "& input": {
                            fontSize: enModal ? "1.25rem" : "1.5rem",
                            fontWeight: 600,
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          m: 0,
                          mt: 0,
                        },
                      }}
                    />
                  </Box>
                  
                  {/* Pasajeros del grupo */}
                  <Box sx={{ flex: 1, p: 0, m: 0, mb: 0 }}>
                    {!enModal && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Cantidad de pasajeros
                        </Typography>
                      </Box>
                    )}
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="text"
                      label={enModal ? "Pasajeros" : undefined}
                      value={stockEditadoDisplay}
                      onChange={(e) => {
                        const inputValue = e.target.value
                        const { locale, decimalSeparator } = localeConfig

                        if (inputValue === "") {
                          setStockEditadoDisplay("")
                          setStockEditado("")
                          setStockEditadoFlag(true)
                          return
                        }

                        const parsedValue = parseLocalizedNumber(inputValue, locale)

                        if (parsedValue === null && inputValue.trim() !== "") {
                          return // Mantener valor anterior si no se puede parsear
                        }

                        let formattedValue = parsedValue !== null
                          ? formatInputWithSeparators(parsedValue, locale)
                          : ""

                        const hasDecimalSeparator = inputValue.includes(decimalSeparator)
                        const endsWithDecimal = inputValue.endsWith(decimalSeparator)
                        const [, fractionalPartOriginal] = inputValue.split(decimalSeparator)

                        // Manejo especial para separador decimal al final o durante entrada fraccional
                        if (endsWithDecimal && !formattedValue.endsWith(decimalSeparator)) {
                          formattedValue += decimalSeparator
                        } else if (hasDecimalSeparator && fractionalPartOriginal !== undefined) {
                          const cleanedFraction = fractionalPartOriginal.replace(/\D/g, "").slice(0, 6)
                          const integerPart = formattedValue.split(decimalSeparator)[0]
                          formattedValue = `${integerPart}${decimalSeparator}${cleanedFraction}`
                        }

                        setStockEditadoDisplay(formattedValue)
                        setStockEditado(parsedValue !== null ? parsedValue : "")
                        setStockEditadoFlag(true)

                        // Mantener cursor al final
                        requestAnimationFrame(() => {
                          const input = e.target
                          if (input) {
                            input.setSelectionRange(formattedValue.length, formattedValue.length)
                          }
                        })
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <GroupsIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        m: 0,
                        mb: 0,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1.5,
                          backgroundColor: stockEditadoFlag 
                            ? alpha(theme.palette.primary.main, 0.15)
                            : alpha(theme.palette.primary.main, 0.05),
                          fontWeight: 600,
                          fontSize: enModal ? "1.25rem" : "1.5rem",
                          height: enModal ? "48px" : "40px",
                          margin: 0,
                          "& input": {
                            fontSize: enModal ? "1.25rem" : "1.5rem",
                            fontWeight: 600,
                            padding: enModal ? "10px 14px" : "8px 14px",
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          m: 0,
                          mt: 0,
                        },
                      }}
                    />
                  </Box>
                </Box>
                )}

                {/* Bloques de Información Financiera - Solo fuera de modal */}
                {!enModal && ingredientes.length > 0 && (
                  <>
                    <Grid container spacing={1.5} sx={{ width: "100%", maxWidth: "100%" }}>
                      {/* 1. Precio Total */}
                      <Grid item xs={6} sm={3}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                            border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                            textAlign: "center",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 0.5 }}>
                            <MonetizationOnIcon sx={{ fontSize: 20, color: theme.palette.success.main, mr: 0.5 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Precio Total
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                            {formatearMoneda(calcularIngresoTotal())}
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* 2. Costo Total Servicio */}
                      <Grid item xs={6} sm={3}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
                            border: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                            textAlign: "center",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 0.5 }}>
                            <AttachMoneyIcon sx={{ fontSize: 20, color: theme.palette.warning.main, mr: 0.5 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Costo Total
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                            {formatearMoneda(calcularCostoTotalServicios())}
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* 3. Ganancia */}
                      <Grid item xs={6} sm={3}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: calcularGanancia() >= 0
                              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
                              : `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                            border: calcularGanancia() >= 0
                              ? `2px solid ${alpha(theme.palette.primary.main, 0.3)}`
                              : `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
                            textAlign: "center",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 0.5 }}>
                            <AccountBalanceWalletIcon
                              sx={{
                                fontSize: 20,
                                color: calcularGanancia() >= 0 ? theme.palette.primary.main : theme.palette.error.main,
                                mr: 0.5,
                              }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Ganancia
                            </Typography>
                          </Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: calcularGanancia() >= 0 ? theme.palette.primary.main : theme.palette.error.main,
                            }}
                          >
                            {formatearMoneda(calcularGanancia())}
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* 4. Porcentaje de Ganancias */}
                      <Grid item xs={6} sm={3}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                            border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                            textAlign: "center",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 0.5 }}>
                            <PercentIcon sx={{ fontSize: 20, color: theme.palette.secondary.main, mr: 0.5 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              % Ganancia
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                            {calcularMargenGanancia().toFixed(1)}%
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  color: theme.palette.text.secondary,
                }}
              >
                <GroupsIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="body1" gutterBottom>
                  Busca un grupo para iniciar servicios en el mismo
                </Typography>
                <Typography variant="body2">
                  El primer grupo que agregues se convertirá en la base de tu servicio
                </Typography>
              </Box>
            )}

          </CardContent>
        </Card>
        )}

        {/* Ingredientes */}
        <Card 
          elevation={2} 
          sx={{ 
            width: "100%", 
            borderRadius: enModal ? 1.5 : 3,
            opacity: !baseItem ? 0.5 : 1,
            pointerEvents: !baseItem ? "none" : "auto",
            border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
            backgroundColor: !baseItem ? alpha(theme.palette.grey[300], 0.1) : alpha(theme.palette.grey[50], 0.5),
            maxWidth: "100%",
            overflow: "visible", // ✅ Visible para no cortar contenido
            flex: enModal ? "1 1 auto" : "0 0 auto",
            display: "flex",
            flexDirection: "column",
            minHeight: enModal ? 0 : "auto",
            maxHeight: enModal ? "100%" : "none",
          }}
        >
          <CardHeader
            avatar={
              <Avatar sx={{ bgcolor: !baseItem ? theme.palette.grey[400] : theme.palette.secondary.main, width: enModal ? 32 : 40, height: enModal ? 32 : 40 }}>
                <BallotIcon sx={{ fontSize: enModal ? "1rem" : "1.5rem" }} />
              </Avatar>
            }
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant={enModal ? "body2" : "h6"} sx={{ fontWeight: 600 }}>
                  Servicios del grupo
                </Typography>
                <Chip size="small" label={ingredientes.length} color={!baseItem ? "default" : "primary"} sx={{ height: enModal ? 20 : 24 }} />
                {!baseItem && <Chip size="small" label="Bloqueado" color="error" variant="outlined" sx={{ height: enModal ? 20 : 24 }} />}
              </Box>
            }
            subheader={!baseItem ? "Primero selecciona un grupo base" : null}
            sx={{ pb: enModal ? 0.5 : 1, pt: enModal ? 1 : 2, "& .MuiCardHeader-title": { fontSize: enModal ? "0.875rem" : "1.25rem" } }}
          />
          <Divider />
          <CardContent sx={{ p: 0, flex: enModal ? "1 1 auto" : 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
            {ingredientes.length > 0 ? (
              <TableContainer sx={{ overflowX: "auto", overflowY: enModal ? "auto" : "auto", maxWidth: "100%", flex: enModal ? "1 1 auto" : 1, minHeight: 0 }}>
                <Table size="small" sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Servicio</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: "left", minWidth: 180 }}>Proveedor</TableCell>
                      {!ocultarCantidadYCostoXCantidad && (
                        <TableCell sx={{ fontWeight: 600, textAlign: "center", minWidth: 150 }}>Cantidad</TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 600, textAlign: "center", minWidth: 170 }}>Costo total</TableCell>
                      {!ocultarCantidadYCostoXCantidad && (
                        <TableCell sx={{ fontWeight: 600, textAlign: "center", minWidth: 150 }}>Costo x cantidad</TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 600, textAlign: "center", minWidth: 80 }}>Acción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ingredientes.map((item) => {
                      const key = `${item.idItem}-${selectedUbicacion?.id || 1}`
                      const cantidad = ocultarCantidadYCostoXCantidad ? "1" : (cantidades[key] || "")
                      const isValidQuantity = ocultarCantidadYCostoXCantidad ? true : (Number.parseFloat(cantidad) > 0)
                      
                      const precioCostoData = preciosCostos[key] || { costo: 0 }
                      const costoActual =
                        costosEditados[key] !== undefined ? costosEditados[key] : precioCostoData.costo
                      
                      // Debug: Ver si el servicio NO tiene costo cargado
                      if (!preciosCostos[key] || preciosCostos[key].costo === 0) {
                        console.warn(`⚠️ Servicio ${item.descripcion} (${key}) NO tiene costo en preciosCostos`)
                      }
                      
                      const qty = ocultarCantidadYCostoXCantidad ? 1 : Number.parseFloat(cantidad || 0)
                      const subtotalCosto = (Number.isFinite(qty) ? qty : 0) * Number.parseFloat(costoActual || 0)

                      return (
                        <TableRow
                          key={key}
                          sx={{
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.02),
                            },
                            backgroundColor: !isValidQuantity ? alpha(theme.palette.warning.main, 0.05) : "inherit",
                          }}
                        >
                          <TableCell sx={{ minWidth: 160 }}>
                            <Tooltip title={item.descripcion || ""}>
                              <Chip size="small" label={item.codigo || item.descripcion || ""} variant="outlined" />
                            </Tooltip>
                          </TableCell>
                          
                          {/* Columna de Proveedor */}
                          <TableCell sx={{ minWidth: 180 }}>
                            <Autocomplete
                              size="small"
                              options={proveedoresDisponibles || []}
                              loading={loadingProveedores}
                              getOptionLabel={(opt) => {
                                if (!opt) return ""
                                const partes = []
                                if (opt.razonSocial) partes.push(opt.razonSocial)
                                if (opt.nombre) partes.push(opt.nombre)
                                if (opt.descripcion) partes.push(opt.descripcion)
                                return partes.join(" - ") || "Sin nombre"
                              }}
                              isOptionEqualToValue={(option, value) => option.id === value?.id}
                              filterOptions={(options) => options}
                              noOptionsText="Escribe 3+ letras para buscar"
                              loadingText="Buscando proveedores..."
                              value={proveedoresDisponibles?.find((e) => e.id === (item.idEntidad || entidadesServicios[item.idItem])) || null}
                              onChange={async (e, newValue) => {
                                if (bloqueado) return
                                
                                const nuevoIdEntidad = newValue ? newValue.id : 2
                                
                                // Agregar el nuevo proveedor a la lista si no está
                                if (newValue && !proveedoresDisponibles.some(p => p.id === newValue.id)) {
                                  setProveedoresDisponibles((prev) => [...prev, newValue])
                                }
                                
                                // Actualizar ingrediente específico
                                setIngredientes((prev) =>
                                  prev.map((ing) =>
                                    ing.idItem === item.idItem ? { ...ing, idEntidad: nuevoIdEntidad } : ing
                                  )
                                )
                                
                                setEntidadesServicios((prev) => ({ ...prev, [item.idItem]: nuevoIdEntidad }))
                                
                                // Cargar costo desde ListaDeMontos con el nuevo proveedor
                                await cargarPreciosCostosServicios([{ ...item, idEntidad: nuevoIdEntidad }], selectedUbicacion?.id || 1)
                                
                                // Limpiar el campo de costo editado manualmente
                                setCostosEditados((prev) => {
                                  const key = `${item.idItem}-${selectedUbicacion?.id || 1}`
                                  const newState = { ...prev }
                                  delete newState[key]
                                  return newState
                                })
                              }}
                              onInputChange={(event, newInputValue, reason) => {
                                // Buscar proveedores cuando el usuario escribe
                                if (reason === "input" && newInputValue && newInputValue.length >= 3) {
                                  console.log('🔍 Buscando proveedores con:', newInputValue)
                                  setLoadingProveedores(true)
                                  getEntidadesClientesTresLetras(newInputValue, 2)
                                }
                              }}
                              disabled={bloqueado}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Escribe para buscar..."
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                      <>
                                        <InputAdornment position="start">
                                          <BusinessIcon sx={{ fontSize: 16 }} color="action" />
                                        </InputAdornment>
                                        {params.InputProps.startAdornment}
                                      </>
                                    ),
                                  }}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 1,
                                      fontSize: "0.813rem",
                                    },
                                  }}
                                />
                              )}
                            />
                          </TableCell>
                          
                          {!ocultarCantidadYCostoXCantidad && (
                            <TableCell sx={{ textAlign: "center", minWidth: 150 }}>
                              <Tooltip title={`Cantidad del servicio "${item.descripcion}" en el paquete`}>
                                <TextField
                                  variant="outlined"
                                  size="small"
                                  value={cantidad === "0" || cantidad === "" ? "" : cantidad}
                                  onChange={(e) => {
                                    const value = e.target.value
                                    setCantidades((prev) => ({ ...prev, [key]: value === "" ? "" : value }))
                                  }}
                                  placeholder="0,00"
                                  type="number"
                                  inputProps={{
                                    inputMode: "decimal",
                                    step: "0.01",
                                    min: "0",
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <ScaleIcon fontSize="small" color="action" />
                                      </InputAdornment>
                                    ),
                                  }}
                                  error={!isValidQuantity && cantidad !== ""}
                                  sx={{
                                    width: 120,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 2,
                                    },
                                  }}
                                />
                              </Tooltip>
                            </TableCell>
                          )}
                          <TableCell sx={{ textAlign: "center", minWidth: 170 }}>
                            <Tooltip
                              title={
                                ocultarCantidadYCostoXCantidad
                                  ? `Costo total del servicio \"${item.descripcion}\"`
                                  : `Costo total (por unidad) del servicio \"${item.descripcion}\"`
                              }
                            >
                            <TextField
                              variant="outlined"
                              size="small"
                                value={costoActual === 0 || costoActual === "" ? "" : costoActual}
                                onChange={(e) => {
                                  const value = e.target.value
                                  setCostosEditados((prev) => ({ ...prev, [key]: value === "" ? "" : value }))
                                }}
                              placeholder="0,00"
                              type="number"
                              inputProps={{
                                inputMode: "decimal",
                                step: "0.01",
                                min: "0",
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <AttachMoneyIcon fontSize="small" color="warning" />
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                width: 140,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                    backgroundColor: costosEditados[key]
                                      ? alpha(theme.palette.warning.main, 0.1)
                                      : "transparent",
                                },
                              }}
                            />
                            </Tooltip>
                          </TableCell>
                          {!ocultarCantidadYCostoXCantidad && (
                            <TableCell sx={{ textAlign: "center", minWidth: 150 }}>
                              <Box>
                                <Typography variant="body2" color="warning.main" sx={{ fontWeight: 700 }}>
                                  {formatearMoneda(subtotalCosto)}
                                </Typography>
                              </Box>
                            </TableCell>
                          )}
                          <TableCell sx={{ textAlign: "center", minWidth: 80 }}>
                            <Tooltip title="Eliminar servicio">
                              <IconButton
                                size="small"
                                disabled={bloqueado}
                                onClick={() => handleRemoveIngredient(key)}
                                sx={{
                                  color: theme.palette.error.main,
                                  "&:hover": {
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  color: theme.palette.text.secondary,
                }}
              >
                <BallotIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="body1" gutterBottom>
                  No hay servicios agregados
                </Typography>
                <Typography variant="body2">Usa el buscador para agregar servicios a tu grupo</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Botones de acción - Arriba de los analíticos */}
      <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2, width: "100%", maxWidth: "100%" }}>
        {/* Botón crear/actualizar paquete */}
        <Button
          variant="contained"
          size="large"
          onClick={crearReceta}
          disabled={loading || !validarFormulario() || bloqueado}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : recetaExistente ? <UpdateIcon /> : <SaveIcon />
          }
          sx={{
            borderRadius: 3,
            textTransform: "none",
            boxShadow: 3,
            py: 1.5,
            px: 4,
            fontSize: "1rem",
            fontWeight: 600,
            backgroundColor: validarFormulario() ? "#5b5b5f" : alpha("#5b5b5f", 0.6),
            "&:hover": {
              backgroundColor: validarFormulario() ? alpha("#5b5b5f", 0.9) : alpha("#5b5b5f", 0.6),
              transform: validarFormulario() ? "translateY(-2px)" : "none",
              boxShadow: validarFormulario() ? 4 : 3,
            },
            "&:disabled": {
              backgroundColor: alpha("#5b5b5f", 0.6),
            },
            transition: "all 0.3s ease",
          }}
        >
          {loading ? "Procesando..." : modoEdicion ? "Editar Paquete" : recetaExistente ? "Actualizar Paquete" : "Crear Paquete"}
        </Button>

        {/* Botón Abrir/Cerrar (solo en modo edición) */}
        {modoEdicion && (
          <Button
            variant={paqueteCerrado ? "outlined" : "contained"}
            size="large"
            onClick={toggleCerradoPaquete}
            disabled={loadingCerrado}
            startIcon={loadingCerrado ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              borderRadius: 3,
              textTransform: "none",
              boxShadow: paqueteCerrado ? 0 : 3,
              py: 1.5,
              px: 4,
              fontSize: "1rem",
              fontWeight: 700,
              borderColor: paqueteCerrado ? theme.palette.success.main : undefined,
              color: paqueteCerrado ? theme.palette.success.main : "white",
              backgroundColor: paqueteCerrado ? "transparent" : theme.palette.error.main,
              "&:hover": {
                backgroundColor: paqueteCerrado
                  ? alpha(theme.palette.success.main, 0.08)
                  : alpha(theme.palette.error.main, 0.9),
              },
            }}
          >
            {paqueteCerrado ? "Abrir Paquete" : "Cerrar Paquete"}
          </Button>
        )}
      </Box>

      {!enModal && mostrarAnaliticos && baseItem && ingredientes.length > 0 && (
        <Box sx={{ mt: 2, width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
          {/* Header de Analíticos */}
          <Card
            elevation={4}
            sx={{
              mb: 2,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
              width: "100%",
              maxWidth: "100%",
            }}
          >
          <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 56, height: 56 }}>
                    <AssessmentIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                      Análisis Financiero Detallado
              </Typography>
                  <Typography variant="body2" color="text.secondary">
                      Proyecciones de ventas, rentabilidad y análisis de costos
                  </Typography>
                </Box>
                </Box>
                <Chip
                  icon={<ShowChartIcon />}
                  label="Datos en Tiempo Real"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Métricas Principales - KPIs */}
          <Grid container spacing={3} sx={{ mb: 3, width: "100%", maxWidth: "100%" }}>
            {/* Ventas Posibles */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={3}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
                  border: `2px solid ${alpha(theme.palette.info.main, 0.3)}`,
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.info.main, width: 48, height: 48 }}>
                    <InventoryIcon />
                  </Avatar>
                  <Chip size="small" label="Stock" color="info" />
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.info.main, mb: 1 }}>
                  {calcularVentasPosibles()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Paquetes Disponibles
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Cantidad máxima de ventas posibles
                </Typography>
              </Card>
            </Grid>

            {/* Ingreso Total Potencial */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={3}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                  border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.success.main, width: 48, height: 48 }}>
                    <MonetizationOnIcon />
                  </Avatar>
                  <Chip size="small" label="Ingresos" color="success" />
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.success.main, mb: 1 }}>
                  {formatearMoneda(calcularIngresoTotal())}
                  </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Ingreso Total Potencial
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Si se venden todos los paquetes
                </Typography>
              </Card>
            </Grid>

            {/* Ganancia Total Potencial */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={3}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background:
                    calcularGananciaTotalPotencial() >= 0
                      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
                      : `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                  border:
                    calcularGananciaTotalPotencial() >= 0
                      ? `2px solid ${alpha(theme.palette.primary.main, 0.3)}`
                      : `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor:
                        calcularGananciaTotalPotencial() >= 0 ? theme.palette.primary.main : theme.palette.error.main,
                      width: 48,
                      height: 48,
                    }}
                  >
                    <AccountBalanceWalletIcon />
                  </Avatar>
                  <Chip
                    size="small"
                    label="Ganancia"
                    color={calcularGananciaTotalPotencial() >= 0 ? "primary" : "error"}
                  />
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                  fontWeight: 700, 
                    color:
                      calcularGananciaTotalPotencial() >= 0 ? theme.palette.primary.main : theme.palette.error.main,
                    mb: 1,
                  }}
                >
                  {formatearMoneda(calcularGananciaTotalPotencial())}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Ganancia Total Potencial
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Beneficio neto estimado
                </Typography>
              </Card>
            </Grid>

            {/* ROI */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={3}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                  border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 48, height: 48 }}>
                    <TrendingUpIcon />
                  </Avatar>
                  <Chip size="small" label="ROI" color="secondary" />
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.secondary.main, mb: 1 }}>
                  {calcularROI().toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Retorno de Inversión
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Rentabilidad sobre costos
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Métricas Secundarias */}
          <Grid container spacing={3} sx={{ mb: 3, width: "100%", maxWidth: "100%" }}>
            {/* Costo Unitario */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={2}
                sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}` }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                  <AttachMoneyIcon sx={{ color: theme.palette.warning.main, fontSize: 28 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Costo total
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                  {formatearMoneda(calcularCostoTotal())}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Costo por paquete individual
                </Typography>
              </Paper>
            </Grid>

            {/* Ganancia Unitaria */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={2}
                sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                  <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 28 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Ganancia Unitaria
                  </Typography>
            </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                  {formatearMoneda(calcularGanancia())}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Beneficio por paquete vendido
                </Typography>
              </Paper>
            </Grid>

            {/* Margen de Ganancia */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={2}
                sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                  <PercentIcon sx={{ color: theme.palette.info.main, fontSize: 28 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Margen de Ganancia
              </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                  {calcularMargenGanancia().toFixed(1)}%
                </Typography>
                  <Typography variant="caption" color="text.secondary">
                  Porcentaje sobre precio de venta
                  </Typography>
              </Paper>
            </Grid>

            {/* Punto de Equilibrio */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={2}
                sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}` }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                  <TimelineIcon sx={{ color: theme.palette.secondary.main, fontSize: 28 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Punto de Equilibrio
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                  {calcularPuntoEquilibrio()} unidades
                </Typography>
                  <Typography variant="caption" color="text.secondary">
                  Ventas necesarias para cubrir costos
                  </Typography>
              </Paper>
            </Grid>

            {/* Costo Total Inventario */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={2}
                sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${alpha(theme.palette.error.main, 0.2)}` }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                  <CalculateIcon sx={{ color: theme.palette.error.main, fontSize: 28 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Inversión Total
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.error.main }}>
                  {formatearMoneda(calcularCostoTotalInventario())}
                </Typography>
                  <Typography variant="caption" color="text.secondary">
                  Costo total del inventario
                  </Typography>
              </Paper>
            </Grid>

            {/* Porcentaje de Ganancia sobre Costo */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={2}
                sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                  <PercentIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    % Ganancia sobre Costo
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {calcularPorcentajeGanancia().toFixed(1)}%
                </Typography>
                  <Typography variant="caption" color="text.secondary">
                  Markup sobre costo unitario
                  </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Gráficas - Sección 1: Comparación y Distribución */}
          <Grid container spacing={3} sx={{ mb: 3, width: "100%", maxWidth: "100%" }}>
            {/* Gráfica de Barras - Comparación Precio vs Costo vs Ganancia */}
            <Grid item xs={12} md={6}>
              <Card elevation={3} sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                  <ShowChartIcon sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Análisis de Precio Unitario
                  </Typography>
                </Box>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={obtenerDatosComparacion()}>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                    <XAxis dataKey="name" tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                    <YAxis
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      label={{
                        value: "Monto ($)",
                        angle: -90,
                        position: "insideLeft",
                        style: { fill: theme.palette.text.secondary },
                      }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                      }}
                      formatter={(value) => formatearMoneda(Number(value))}
                    />
                    <Legend />
                    <Bar dataKey="value" fill={theme.palette.primary.main} radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 2, textAlign: "center" }}
                >
                  Comparación entre precio de venta, costo total y ganancia por unidad
                </Typography>
              </Card>
            </Grid>

            {/* Gráfica de Pie - Distribución de Costos por Servicio */}
            <Grid item xs={12} md={6}>
              <Card elevation={3} sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                  <PieChartIcon sx={{ color: theme.palette.secondary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Distribución de Costos
                  </Typography>
              </Box>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPie>
                    <Pie
                      data={obtenerDatosDistribucionCostos()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, porcentaje }) => `${name}: ${porcentaje}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {obtenerDatosDistribucionCostos().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                      }}
                      formatter={(value, name, props) => [
                        `${formatearMoneda(Number(value))} (${props.payload.porcentaje}%)`,
                        "Costo",
                      ]}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 2, textAlign: "center" }}
                >
                  Porcentaje de costo que representa cada servicio en el paquete
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Gráficas - Sección 2: Proyección y Margen */}
          <Grid container spacing={3} sx={{ mb: 3, width: "100%", maxWidth: "100%" }}>
            {/* Gráfica de Líneas - Proyección de Ventas */}
            <Grid item xs={12} md={8}>
              <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                  <TimelineIcon sx={{ color: theme.palette.success.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Proyección de Ingresos y Ganancias
                  </Typography>
            </Box>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={obtenerDatosProyeccionVentas()}>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                    <XAxis
                      dataKey="unidades"
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      label={{
                        value: "Unidades Vendidas",
                        position: "insideBottom",
                        offset: -5,
                        style: { fill: theme.palette.text.secondary },
                      }}
                    />
                    <YAxis
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      label={{
                        value: "Monto ($)",
                        angle: -90,
                        position: "insideLeft",
                        style: { fill: theme.palette.text.secondary },
                      }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                      }}
                      formatter={(value) => formatearMoneda(Number(value))}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="ingresos"
                      stroke={theme.palette.success.main}
                      strokeWidth={3}
                      name="Ingresos"
                      dot={{ fill: theme.palette.success.main, r: 5 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="costos"
                      stroke={theme.palette.error.main}
                      strokeWidth={3}
                      name="Costos"
                      dot={{ fill: theme.palette.error.main, r: 5 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="ganancia"
                      stroke={theme.palette.primary.main}
                      strokeWidth={3}
                      name="Ganancia"
                      dot={{ fill: theme.palette.primary.main, r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 2, textAlign: "center" }}
                >
                  Proyección de ingresos, costos y ganancias según cantidad de paquetes vendidos
                </Typography>
        </Card>
            </Grid>

            {/* Gráfica Radial - Margen de Ganancia */}
            <Grid item xs={12} md={4}>
              <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                  <PercentIcon sx={{ color: theme.palette.info.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Margen de Ganancia
                  </Typography>
                </Box>
                <ResponsiveContainer width="100%" height={350}>
                  <RadialBarChart
                    cx="50%"
                    cy="50%"
                    innerRadius="60%"
                    outerRadius="100%"
                    data={obtenerDatosMargenGanancia()}
                    startAngle={180}
                    endAngle={0}
                  >
                    <RadialBar minAngle={15} background clockWise dataKey="value" cornerRadius={10} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                      }}
                      formatter={(value) => `${Number(value).toFixed(1)}%`}
                    />
                  </RadialBarChart>
                </ResponsiveContainer>
                <Box sx={{ textAlign: "center", mt: -8 }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: obtenerDatosMargenGanancia()[0].fill }}>
                    {calcularMargenGanancia().toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Margen sobre precio
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 4, textAlign: "center" }}
                >
                  {calcularMargenGanancia() >= 30
                    ? "✅ Excelente margen"
                    : calcularMargenGanancia() >= 15
                      ? "⚠️ Margen aceptable"
                      : "❌ Margen bajo"}
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Resumen Ejecutivo */}
          <Card
            elevation={4}
            sx={{
              p: 3,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              width: "100%",
              maxWidth: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }}>
                <InfoIcon />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Resumen Ejecutivo del Paquete
              </Typography>
            </Box>

            <Grid container spacing={3} sx={{ width: "100%", maxWidth: "100%" }}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={1}
                  sx={{ p: 2.5, borderRadius: 2, backgroundColor: alpha(theme.palette.background.paper, 0.8) }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                    📊 Análisis de Rentabilidad
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Precio de venta unitario:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                      {formatearMoneda(precioFinalPaquete)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Costo total:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
                      {formatearMoneda(calcularCostoTotal())}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Ganancia por unidad:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: calcularGanancia() >= 0 ? theme.palette.primary.main : theme.palette.error.main,
                      }}
                    >
                      {formatearMoneda(calcularGanancia())}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Margen de ganancia:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                      {calcularMargenGanancia().toFixed(1)}%
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                      ROI estimado:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.secondary.main }}>
                      {calcularROI().toFixed(1)}%
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  elevation={1}
                  sx={{ p: 2.5, borderRadius: 2, backgroundColor: alpha(theme.palette.background.paper, 0.8) }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                    💰 Proyección de Ventas Totales
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Stock disponible:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                      {calcularVentasPosibles()} paquetes
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Ingresos potenciales:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                      ${calcularIngresoTotal().toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Inversión total:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
                      {formatearMoneda(calcularCostoTotalInventario())}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Ganancia total potencial:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color:
                          calcularGananciaTotalPotencial() >= 0 ? theme.palette.primary.main : theme.palette.error.main,
                      }}
                    >
                      {formatearMoneda(calcularGananciaTotalPotencial())}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                      Punto de equilibrio:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.secondary.main }}>
                      {calcularPuntoEquilibrio()} unidades
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Alertas y Recomendaciones */}
            <Box sx={{ mt: 3 }}>
              {calcularMargenGanancia() < 15 && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ⚠️ Margen de ganancia bajo ({calcularMargenGanancia().toFixed(1)}%)
                  </Typography>
                  <Typography variant="caption">
                    Considera aumentar el precio de venta o reducir costos para mejorar la rentabilidad.
                  </Typography>
                </Alert>
              )}

              {calcularGanancia() < 0 && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ❌ Pérdida por unidad ({formatearMoneda(Math.abs(calcularGanancia()))})
                  </Typography>
                  <Typography variant="caption">
                    El precio de venta es menor que el costo. Ajusta los precios urgentemente.
                  </Typography>
                </Alert>
              )}

              {calcularMargenGanancia() >= 30 && (
                <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ✅ Excelente margen de ganancia ({calcularMargenGanancia().toFixed(1)}%)
                  </Typography>
                  <Typography variant="caption">
                    El paquete tiene una rentabilidad saludable. Considera estrategias de volumen.
                  </Typography>
                </Alert>
              )}

              {calcularVentasPosibles() > 0 && calcularPuntoEquilibrio() > calcularVentasPosibles() && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ⚠️ Stock insuficiente para alcanzar punto de equilibrio
                  </Typography>
                  <Typography variant="caption">
                    Necesitas {calcularPuntoEquilibrio()} ventas para cubrir costos, pero solo tienes{" "}
                    {calcularVentasPosibles()} en stock.
                  </Typography>
                </Alert>
              )}
            </Box>
          </Card>
        </Box>
      )}

      {/* Validaciones y alertas */}
      {baseItem && ingredientes.length > 0 && !validarCantidades() && (
        <Box sx={{ mt: 2, width: "100%", maxWidth: "100%" }}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <Typography variant="body2">
              ⚠️ Algunos servicios no tienen cantidad válida. Asegúrate de que todas las cantidades sean mayores a 0.
            </Typography>
          </Alert>
        </Box>
      )}

      {/* 🔧 VALIDACIÓN DE FECHAS */}
      {baseItem && ingredientes.length > 0 && !validarFechas() && (
        <Box sx={{ mt: 2, width: "100%", maxWidth: "100%" }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            <Typography variant="body2">
              ❌ Error en las fechas:{" "}
              {!fechaInicio
                ? "La fecha inicio es obligatoria"
                : "La fecha fin no puede ser menor a la Fecha Inicio"}
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Alertas de estado */}
      <Fade in={success}>
        <Alert
          severity="success"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 9999,
            borderRadius: 2,
            boxShadow: 3,
          }}
          onClose={() => setSuccess(false)}
        >
          {recetaExistente ? "Paquete Actualizado" : "Paquete Creado"}
        </Alert>
      </Fade>

      {error && (
        <Alert
          severity="error"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 9999,
            borderRadius: 2,
            boxShadow: 3,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
    </Box>
  )
}

export default RecetaComponent
